<!-- eslint-disable vue/no-side-effects-in-computed-properties -->
<template>
  <!-- Modern Header -->
  <nav
    class="fixed top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 dark:bg-bgLightDark/95 dark:border-gray-700 shadow-sm"
  >
    <div class="px-4 py-3 lg:px-6">
      <div class="flex items-center justify-between">
        <!-- Left Section -->
        <div class="flex items-center space-x-3">
          <a
            href="/sold-list"
            class="flex items-center justify-center w-10 h-10 rounded-full text-gray-700 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            :title="$t('sale')"
          >
            <i class="fa-solid fa-chevron-right text-lg"></i>
          </a>
          <span class="text-base sm:text-lg font-semibold text-textBlack dark:text-textWhite">
            {{ $t("new_sale") }}
          </span>

          <!-- Dollar Price Badge -->
          <div
            class="hidden sm:flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full"
          >
            <i
              class="fa-solid fa-dollar-sign text-blue-600 dark:text-blue-400 text-sm"
            ></i>
            <span class="text-sm font-medium text-blue-700 dark:text-blue-300">
              {{ $t("dolar_price") }}: {{ formatNumber(getDolarPrice) }}
            </span>
          </div>
        </div>

        <!-- Right Section -->
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition focus:outline-none focus:ring-2 focus:ring-green-400"
            :title="$t('create_order')"
            @click="showWhatsAppDialog"
          >
            <i class="fa-brands fa-whatsapp text-lg"></i>
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition focus:outline-none focus:ring-2 focus:ring-violet-400"
            :title="$t('customize_layout')"
            @click="showUiCustomizer = true"
          >
            <i class="fa-solid fa-sliders"></i>
          </button>
          <!-- Prev invoice navigation -->
          <button
            type="button"
            class="inline-flex items-center justify-center min-w-[120px] h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500 transition px-3"
            :title="$t('prev')"
            @click="goToPrevInvoice"
            v-if="
              $store.getters['checkUserAccess']({
                id: '19',
                action: 'edit',
              })
            "
          >
            <!-- Reversed arrow icon for POS as requested
            <i class="fa-solid" :class="[$store.getters['isRtl'] ? 'fa-angle-left' : 'fa-angle-right']"></i>
         -->
            <span>{{ $t("prev_invoice") }}</span>
          </button>
          <!-- Calculator Button -->
          <button
            @click="!isLoadingData && openCalculator()"
            :disabled="isLoadingData"
            :class="[
              'flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500',
              isLoadingData
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:scale-105',
            ]"
            :title="
              isLoadingData ? 'Please wait for page to load' : 'Calculator'
            "
          >
            <i class="fa-solid fa-calculator text-lg"></i>
          </button>

          <!-- Loading Indicator -->
          <div
            v-if="isLoadingData"
            class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400"
          >
            <div
              class="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"
            ></div>
            <span>Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </nav>
  <teleport to="body">
    <div
      v-if="showUiCustomizer"
      class="fixed inset-0 isolate overflow-y-auto z-[2147483646] bg-black/80 backdrop-blur-md flex items-start sm:items-center justify-center p-3 sm:p-6"
      :dir="$store.getters['isRtl'] ? 'rtl' : 'ltr'"
      style="z-index: 2147483646;"
      @click.self="showUiCustomizer = false"
    >
      <div
        :class="[
          'relative mt-4 sm:mt-0 w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark shadow-2xl p-5 sm:p-6 rounded-2xl z-[2147483647] ring-1 ring-white/40 dark:ring-gray-700/60',
          $store.getters['isRtl'] ? 'text-right' : 'text-left',
        ]"
        style="z-index: 2147483647;"
      >
        <div class="flex items-center justify-between pb-3 mb-4 border-b border-gray-200 dark:border-gray-700">
          <p class="font-semibold text-textBlack dark:text-textWhite">
            {{ $t("customize_layout") }}
          </p>
          <button
            class="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            @click="showUiCustomizer = false"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="pt-1 mt-1">
          <p class="text-sm font-medium text-textBlack1 dark:text-textWhite1 mb-2">
            {{ $t("cart_columns") }}
          </p>
          <div class="space-y-2 text-sm">
            <label class="flex items-center justify-between gap-3 cursor-pointer p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-bgLightDark transition">
              <span class="text-textBlack1 dark:text-textWhite1">{{ $t("show_bonus_column") }}</span>
              <input type="checkbox" v-model="showCartBonusColumn" class="h-4 w-4 rounded" />
            </label>
            <label class="flex items-center justify-between gap-3 cursor-pointer p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-bgLightDark transition">
              <span class="text-textBlack1 dark:text-textWhite1">{{ $t("show_packages_column") }}</span>
              <input type="checkbox" v-model="showCartPackagesColumn" class="h-4 w-4 rounded" />
            </label>
            <label class="flex items-center justify-between gap-3 cursor-pointer p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-bgLightDark transition">
              <span class="text-textBlack1 dark:text-textWhite1">{{ $t("show_discount_column") }}</span>
              <input type="checkbox" v-model="showCartDiscountColumn" class="h-4 w-4 rounded" />
            </label>
          </div>
        </div>
        <div class="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
          <p class="text-sm font-medium text-textBlack1 dark:text-textWhite1 mb-2">
            {{ $t("ui_style") }}
          </p>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              class="h-10 rounded-lg border text-sm transition"
              :class="
                uiDesignMode === 'classic'
                  ? 'border-primaryColor bg-primaryColor/10 text-primaryColor'
                  : 'border-gray-300 dark:border-gray-600 text-textBlack1 dark:text-textWhite1 hover:bg-slate-50 dark:hover:bg-bgLightDark'
              "
              @click="uiDesignMode = 'classic'"
            >
              {{ $t("ui_classic") }}
            </button>
            <button
              type="button"
              class="h-10 rounded-lg border text-sm transition"
              :class="
                uiDesignMode === 'green'
                  ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                  : 'border-gray-300 dark:border-gray-600 text-textBlack1 dark:text-textWhite1 hover:bg-slate-50 dark:hover:bg-bgLightDark'
              "
              @click="uiDesignMode = 'green'"
            >
              {{ $t("ui_green") }}
            </button>
          </div>
        </div>
        <div class="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            class="w-full h-10 rounded-xl bg-primaryColor text-white text-sm font-medium hover:opacity-90 transition"
            @click="showUiCustomizer = false"
          >
            {{ $t("done") }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
  <LoadingProgress v-show="isLoadingData" />

  <div
    :class="[
      'mx-2 mb-3 h-full rounded-2xl transition-colors duration-300',
      uiDesignMode === 'green' ? 'mt-14 px-3 pb-4 pt-5 sm:pt-6' : 'mt-12 p-1',
      uiDesignMode === 'green'
        ? 'bg-gradient-to-b from-green-50 to-emerald-50 dark:from-bgFullDark dark:to-bgLightDark'
        : 'bg-gradient-to-b from-slate-50 to-slate-100 dark:from-bgFullDark dark:to-bgLightDark',
    ]"
  >
    <div
      v-if="uiDesignMode === 'green'"
      :class="[
        'flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-stretch md:gap-4 pb-44 md:pb-2',
        $store.getters['isRtl'] ? 'md:flex-row-reverse' : '',
      ]"
    >
      <!-- LEFT SIDE: MAIN SALES AREA -->
      <div
        class="w-full min-w-0 max-w-full overflow-x-auto md:w-8/12 rounded-2xl border border-blue-200 dark:border-blue-800 bg-gradient-to-b from-blue-50 to-cyan-50 dark:from-bgMeduimDark dark:to-bgLightDark shadow-sm p-3 sm:p-4"
      >
        <div class="mb-2 w-full min-w-0">
          <form @submit="enterBarcode" class="w-full min-w-0">
            <div
              class="relative w-full min-w-0 max-w-full rounded-xl border border-blue-200 bg-blue-50/40 p-2 dark:border-blue-700 dark:bg-bgFullDark/60"
            >
              <!-- One row: flex + inline (Tailwind content was empty, grid arbitrary classes were not in CSS) -->
              <div
                class="green-scan-strip flex w-full min-w-0 max-w-full flex-nowrap items-stretch gap-2"
                style="display: flex; flex-direction: row; flex-wrap: nowrap; align-items: stretch; gap: 0.5rem; width: 100%"
              >
                <div class="relative h-10 min-w-0" style="flex: 1.4 1 0; min-width: 0">
                  <input
                    ref="barcodeInput"
                    v-model="barcode"
                    type="text"
                    class="h-10 w-full min-w-0 rounded-lg border border-blue-200 bg-white px-3 py-2 pr-10 text-sm text-textBlack1 dark:border-blue-700 dark:bg-bgFullDark dark:text-white"
                    :placeholder="$t('pos_scan_or_code')"
                  />
                  <i class="fa-solid fa-barcode pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-blue-600"></i>
                </div>
                <div class="relative h-10 min-w-0" style="flex: 1 1 0; min-width: 0">
                  <input
                    v-model="greenQuickSearchQuery"
                    type="text"
                    class="h-10 w-full min-w-0 rounded-lg border border-blue-200 bg-white px-3 py-2 pr-10 text-sm text-textBlack1 dark:border-blue-700 dark:bg-bgFullDark dark:text-white"
                    :placeholder="$t('search_item')"
                  />
                  <i class="fa-solid fa-search pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-blue-600"></i>
                  <div
                    v-if="showGreenQuickSearchResults"
                    class="absolute z-30 mt-1 w-full min-w-0 max-w-full rounded-xl border border-blue-200 dark:border-blue-700 bg-white dark:bg-bgFullDark shadow-xl max-h-64 overflow-y-auto"
                  >
                    <button
                      v-for="(item, idx) in greenQuickSearchResults"
                      :key="`green-search-${item.i_id}-${idx}`"
                      type="button"
                      class="w-full text-right px-3 py-2 border-b last:border-b-0 border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      @click="selectGreenQuickSearchItem(item)"
                    >
                      <div class="text-sm font-semibold text-textBlack1 dark:text-textWhite1 truncate">{{ item.i_name }}</div>
                      <div class="mt-0.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{{ $t("barcode") }}: {{ item.i_barcode }}</span>
                        <span>{{ $t("remain") }}: {{ item.i_quantity }}</span>
                      </div>
                      <div class="text-xs text-blue-700 dark:text-blue-300 font-semibold mt-0.5">
                        {{ $t("price") }}: {{ formatNumber(item.i_sale_price) }}
                        <span class="mx-1" v-if="item.currency && item.currency.cur_name">{{ $t(item.currency.cur_name) }}</span>
                      </div>
                    </button>
                  </div>
                </div>
                <button
                  id="boxTypeGreen"
                  type="button"
                  class="inline-flex h-10 shrink-0 items-center justify-center gap-1 self-center rounded-lg border border-blue-200 bg-white px-2 text-xs font-medium text-textBlack1 dark:border-blue-700 dark:bg-bgFullDark dark:text-white sm:px-2.5 sm:text-sm"
                  style="flex: 0 0 5.5rem; width: 5.5rem; min-width: 5.5rem; max-width: 5.5rem"
                  @click="changeCurrencyType"
                >
                  <i class="fa-solid fa-arrows-rotate text-blue-600 text-[10px] sm:text-xs shrink-0"></i>
                  <span class="truncate" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                  <span class="truncate" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                </button>
              </div>
            </div>
          </form>
        </div>

        <div class="rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-bgFullDark overflow-hidden">
          <table class="w-full table-fixed text-right text-xs">
            <thead class="bg-blue-100 dark:bg-bgMeduimDark text-blue-900 dark:text-blue-200">
              <tr>
                <th class="px-2 py-2 text-right">{{ $t("item_name") }}</th>
                <th class="px-2 py-2 w-[110px] text-center">{{ $t("type") }}</th>
                <th class="px-2 py-2 w-[90px] text-center">{{ $t("price") }}</th>
                <th class="px-2 py-2 w-[120px] text-center">{{ $t("quantity") }}</th>
                <th v-if="showCartBonusColumn" class="px-2 py-2 w-[95px] text-center">{{ $t("bonus") }}</th>
                <th v-if="showCartPackagesColumn" class="px-2 py-2 w-[120px] text-center">{{ $t("packages") }}</th>
                <th v-if="showCartDiscountColumn" class="px-2 py-2 w-[110px] text-center">{{ $t("discount") }}</th>
                <th class="px-2 py-2 w-[95px] text-center">{{ $t("total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in getSoldListByCurrentCurrency"
                :key="`split-green-row-${index}`"
                @click="selectedGreenRowIndex = index"
                :class="[
                  'border-b border-blue-50 dark:border-gray-700 transition',
                  selectedGreenRowIndex === index ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-bgFullDark',
                ]"
              >
                <td class="px-2 py-1.5">
                  <div class="font-semibold text-textBlack1 dark:text-textWhite1 truncate max-w-[220px]">
                    {{ item.i_name }}
                  </div>
                  <div class="text-[10px] text-gray-500">{{ item.i_barcode }}</div>
                </td>
                <td class="px-2 py-1.5 text-center">
                  <select
                    class="w-full h-7 rounded border border-blue-200 dark:border-blue-700 text-[11px] bg-white dark:bg-bgMeduimDark"
                    v-model.number="item.si_is_package"
                    @change="onItemTypeChange(item)"
                  >
                    <option :value="0">{{ $t("piece") }}</option>
                    <option :value="1" :disabled="!(Number(item.i_package) > 0 && Number(item.i_package_price) > 0)">
                      {{ $t("carton") }}
                    </option>
                  </select>
                </td>
                <td class="px-1 py-1.5 text-center">
                  <input
                    type="text"
                    inputmode="decimal"
                    class="mx-auto block h-7 w-full max-w-[5.5rem] rounded border border-blue-200 bg-white px-1 text-center text-xs font-semibold text-textBlack1 dark:border-blue-700 dark:bg-bgMeduimDark dark:text-white"
                    :value="lineSalePriceInputValue(item)"
                    @focus="selectedGreenRowIndex = index"
                    @input="onLineSalePriceLocaleInput($event, item, { recalcDiscount: true })"
                  />
                </td>
                <td class="px-1 py-1.5">
                  <div class="mx-auto flex w-fit items-center justify-center gap-1 whitespace-nowrap">
                    <button type="button" class="w-6 h-6 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700" @click.stop="updateInvoice(item, Number(item.si_quantity) - 1)">-</button>
                    <input
                      type="text"
                      inputmode="numeric"
                      class="pos-green-qty-input w-14 h-8 rounded border border-blue-300 dark:border-blue-700 text-center text-sm font-semibold text-textBlack1 dark:text-white bg-white dark:bg-bgMeduimDark"
                      :value="String(item.si_quantity ?? 1)"
                      @focus="selectedGreenRowIndex = index"
                      @input="onGreenLineQtyLocaleInput($event, item)"
                    />
                    <button type="button" class="w-6 h-6 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700" @click.stop="updateInvoice(item, Number(item.si_quantity) + 1)">+</button>
                  </div>
                </td>
                <td v-if="showCartBonusColumn" class="px-2 py-1.5 text-center">
                  <input
                    type="text"
                    inputmode="numeric"
                    class="w-14 h-6 rounded border border-blue-200 dark:border-blue-700 text-center text-sm font-medium bg-white dark:bg-bgMeduimDark"
                    :value="String(item.si_bonus_quantity ?? 0)"
                    @input="onLineBonusQtyLocaleInput($event, item)"
                  />
                </td>
                <td v-if="showCartPackagesColumn" class="px-2 py-1.5 text-center">
                  <div v-if="item.i_packages && item.i_packages.length > 0" class="flex flex-wrap justify-center gap-1">
                    <button
                      v-for="(pkg, pkgIndex) in item.i_packages"
                      :key="pkgIndex"
                      type="button"
                      class="px-1.5 h-6 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 text-[10px]"
                      @click.stop="setQuantityFromPackage(item, pkg.quantity, pkg.price)"
                    >
                      {{ pkg.quantity }}+
                    </button>
                  </div>
                  <span v-else class="text-gray-400">-</span>
                </td>
                <td v-if="showCartDiscountColumn" class="px-2 py-1.5 text-center">
                  <input
                    type="text"
                    inputmode="decimal"
                    class="w-20 h-6 rounded border border-blue-200 dark:border-blue-700 text-center text-sm font-medium bg-white dark:bg-bgMeduimDark"
                    :value="String(item.si_discount ?? 0)"
                    @input="onLineDiscountLocaleInput($event, item)"
                  />
                </td>
                <td class="px-2 py-1.5 text-center font-bold text-blue-800 dark:text-blue-300">
                  {{ formatNumber(item.si_quantity * getPriceByUnit(item) - item.si_discount) }}
                </td>
              </tr>
              <tr v-if="getSoldListByCurrentCurrency.length === 0">
                <td :colspan="greenCartColspan" class="px-2 py-8 text-center text-gray-500">
                  {{ $t("no_item_in_invoice") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Left-side summary removed to reduce clutter; summary is on right panel -->
      </div>

      <!-- RIGHT SIDE: COMPACT CLASSIC-LIKE PANEL -->
      <div class="w-full md:w-4/12 rounded-2xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-bgMeduimDark p-4 pt-5 sm:p-5 sm:pt-6 md:pt-7 shadow-sm">
        <div class="rounded-lg border border-blue-200 dark:border-blue-700 bg-white dark:bg-bgLightDark mb-3 overflow-hidden">
          <button
            type="button"
            class="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20"
            @click="isShowGreenRetailDetails = !isShowGreenRetailDetails"
          >
            <span>وردەکاری تاکفرۆشی</span>
            <i
              class="fa-solid fa-chevron-down transition-transform duration-200"
              :class="isShowGreenRetailDetails ? 'rotate-180' : ''"
            ></i>
          </button>
          <div v-if="isShowGreenRetailDetails" class="p-3 border-t border-blue-100 dark:border-blue-800 space-y-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DropdownInput
                v-model="cu_id"
                :label="$t('customer')"
                :placeholder="$t('choose_customer')"
                :items="convertToListIdValueCustomers"
                :rules="rules_dropdown"
                :isError="checkRulesIsError(cu_id, 'add', this.rules_dropdown).isError"
                :errorMsg="checkRulesIsError(cu_id, 'add', this.rules_dropdown).errorMsg"
                @change="getCustomerDebt"
              />
              <DropdownInput
                v-model="bo_id"
                :label="$t('box')"
                :placeholder="$t('choose_box_that_money_get_in')"
                :rules="rules_dropdown"
                :items="convertToListIdValueBoxes"
                :isError="checkRulesIsError(bo_id, 'add', this.rules_dropdown).isError"
                :errorMsg="checkRulesIsError(bo_id, 'add', this.rules_dropdown).errorMsg"
                linkDrop="/all-box"
              />
              <DropdownInput
                v-model="st_id"
                :label="$t('sale_type')"
                :placeholder="$t('choose_sale_type')"
                :rules="rules_dropdown"
                :items="convertToListIdValueSaleTypes"
                :isError="checkRulesIsError(st_id, 'add', this.rules_dropdown).isError"
                :errorMsg="checkRulesIsError(st_id, 'add', this.rules_dropdown).errorMsg"
                linkDrop="/add-sale-type"
              />
              <DropdownInput
                v-model="pl_id"
                :label="$t('sale_platform')"
                :placeholder="$t('choose_sale_platform')"
                :rules="rules_dropdown"
                :items="convertToListIdValuePlatforms"
                :isError="checkRulesIsError(pl_id, 'add', this.rules_dropdown).isError"
                :errorMsg="checkRulesIsError(pl_id, 'add', this.rules_dropdown).errorMsg"
                linkDrop="/sale-platform"
              />
              <BaseInput
                v-model="created_at"
                type="date"
                :label="$t('sale_date')"
                :placeholder="$t('sale_date')"
                :rules="no_rule"
                :isError="checkRulesIsError(created_at, 'add', this.no_rule).isError"
                :errorMsg="checkRulesIsError(created_at, 'add', this.no_rule).errorMsg"
              />
              <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-bgFullDark px-2 py-2">
                <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1.5">
                  {{ $t("invoice_type_sale") }}
                </label>
                <div class="flex items-center gap-3 text-sm">
                  <label class="inline-flex items-center gap-1.5">
                    <input type="radio" value="1" v-model="in_type" name="green-inline-radio-group" @change="getPosData" />
                    <span>{{ $t("sale_tak") }}</span>
                  </label>
                  <label class="inline-flex items-center gap-1.5">
                    <input type="radio" value="2" v-model="in_type" name="green-inline-radio-group" @change="getPosData" />
                    <span>{{ $t("sale_jwmla") }}</span>
                  </label>
                </div>
              </div>
            </div>

            <TextAreaInput
              v-model="in_note"
              :label="$t('note')"
              :placeholder="$t('note')"
              :rules="no_rule"
              :isError="checkRulesIsError(in_note, 'add', this.no_rule).isError"
              :errorMsg="checkRulesIsError(in_note, 'add', this.no_rule).errorMsg"
            />

            <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-bgFullDark px-2 py-2">
              <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1.5">
                {{ $t("do_you_want_print_right_after_sale") }}
              </label>
              <div class="flex items-center gap-3 text-sm flex-wrap">
                <label class="inline-flex items-center gap-1.5">
                  <input type="radio" value="1" v-model="isPrint" name="green-inline-radio-group-print" @change="printStatus" />
                  <span>{{ $t("cashier") }}</span>
                </label>
                <label class="inline-flex items-center gap-1.5">
                  <input type="radio" value="3" v-model="isPrint" name="green-inline-radio-group-print" @change="printStatus" />
                  <span>{{ $t("a4") }}</span>
                </label>
                <label class="inline-flex items-center gap-1.5">
                  <input type="radio" value="2" v-model="isPrint" name="green-inline-radio-group-print" @change="printStatus" />
                  <span>{{ $t("no") }}</span>
                </label>
              </div>
            </div>

            <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-bgFullDark px-2 py-2">
              <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1.5">
                {{ $t("fast_customer") }}
              </label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BaseInput
                  v-model="customer_name"
                  :label="$t('customer_name')"
                  :placeholder="$t('customer_name')"
                  :rules="no_rule"
                  :isError="checkRulesIsError(customer_name, 'add', this.no_rule).isError"
                  :errorMsg="checkRulesIsError(customer_name, 'add', this.no_rule).errorMsg"
                />
                <BaseInput
                  v-model="customer_phone_number"
                  :label="$t('customer_phone_number')"
                  :placeholder="$t('customer_phone_number')"
                  :rules="no_rule"
                  :isError="checkRulesIsError(customer_phone_number, 'add', this.no_rule).isError"
                  :errorMsg="checkRulesIsError(customer_phone_number, 'add', this.no_rule).errorMsg"
                />
              </div>
            </div>

            <div v-show="isShowDebts" class="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs">
              <div class="flex justify-between gap-2">
                <span>{{ $t("total_debt_dinar") }}</span>
                <b :class="Number(beforeDebtIQD) > 0 ? 'text-red-500' : 'text-green-500'">{{ formatNumber(beforeDebtIQD) }}</b>
              </div>
              <div class="flex justify-between gap-2 mt-1">
                <span>{{ $t("total_debt_dollar") }}</span>
                <b :class="Number(beforeDebtUSD) > 0 ? 'text-red-500' : 'text-green-500'">{{ formatNumber(beforeDebtUSD) }}</b>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-3 mb-2">
          <p class="text-sm font-semibold text-blue-800 dark:text-blue-200">{{ $t("total_price") }}</p>
          <p class="text-4xl font-extrabold text-blue-700 dark:text-blue-300 leading-none mt-1">
            {{ formatPriceByCurrency(sumPrice) }}
          </p>
        </div>

        <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgLightDark p-2 mb-2">
          <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1">
            {{ $t("money_amount_received") }}
          </label>
          <input
            type="text"
            inputmode="decimal"
            class="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-slate-50 dark:bg-bgFullDark px-2 text-sm text-textBlack1 dark:text-white"
            :value="String(in_paid ?? 0)"
            @input="onRootDecimalLocaleInput($event, 'in_paid')"
          />
        </div>

        <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgLightDark p-2 mb-2">
          <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1">
            {{ $t("total_discount") }}
          </label>
          <div class="flex items-center gap-2">
            <input
              type="text"
              inputmode="decimal"
              class="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-slate-50 dark:bg-bgFullDark px-2 text-sm text-textBlack1 dark:text-white"
              :value="totalDiscountInputDisplay(total_discount)"
              @input="onTotalDiscountLocaleInput"
            />
            <span class="text-xs whitespace-nowrap" v-show="cur_id == 1">{{ $t("dinar") }}</span>
            <span class="text-xs whitespace-nowrap" v-show="cur_id == 2">{{ $t("dollar") }}</span>
          </div>
        </div>

        <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgLightDark p-2 mb-2">
          <label class="text-xs font-medium text-textBlack1 dark:text-textWhite1 block mb-1">
            {{ $t("percentage_discount") }}
          </label>
          <div class="flex items-center gap-2">
            <input
              type="text"
              inputmode="decimal"
              class="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-slate-50 dark:bg-bgFullDark px-2 text-sm text-textBlack1 dark:text-white"
              :value="String(percentage_discount ?? 0)"
              @input="onPercentageDiscountLocaleInput"
            />
            <span class="text-xs">%</span>
          </div>
        </div>

        <div class="fixed md:static bottom-0 left-0 right-0 z-40 md:z-auto bg-white dark:bg-bgMeduimDark border-t border-blue-200 dark:border-blue-800 md:border-t-0 p-2 md:p-0 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] md:pb-0 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] md:shadow-none">
          <div
            v-if="sumPrice - in_paid > 0"
            class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 mb-2 flex items-center justify-between"
          >
            <span class="text-sm font-semibold text-red-700 dark:text-red-300">{{ $t("debt") }}</span>
            <span class="text-lg font-extrabold text-red-700 dark:text-red-300">
              {{ formatNumber(sumPrice - in_paid) }}
            </span>
          </div>

          <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgLightDark p-3 mb-2 text-sm">
            <div class="flex justify-between py-1">
              <span class="font-medium">{{ $t("total_without_discount") }}</span>
              <b class="text-base">{{ formatPriceByCurrency(sumPriceWithoutDiscount) }}</b>
            </div>
            <div class="flex justify-between py-1">
              <span class="font-medium">{{ $t("discount_amount") }}</span>
              <b class="text-base">{{ formatPriceByCurrency(sumDiscount) }}</b>
            </div>
            <div class="flex justify-between items-center py-1">
              <span class="text-base font-semibold">{{ $t("total_price") }}</span>
              <b class="text-xl font-extrabold text-blue-700 dark:text-blue-300">{{ formatPriceByCurrency(sumPrice) }}</b>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-2">
            <button
              type="button"
              class="h-10 rounded-lg bg-blue-500 text-white text-sm"
              @click="showModal(current)"
            >
              <i class="fa-solid fa-calculator"></i>
            </button>
            <button
              type="button"
              class="h-10 rounded-lg bg-red-500 text-white text-sm"
              @click="clearInvoice(true)"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
            <button
              type="button"
              class="h-10 rounded-lg bg-primaryColor text-white font-bold text-sm"
              @click="addSaleItem"
            >
              <span v-if="!isBtnLoading">{{ $t("sale") }}</span>
              <span v-else>{{ $t("wait") }}</span>
            </button>
            <select
              v-model="printInvoiceLanguage"
              class="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-bgFullDark px-2 text-xs text-textBlack1 dark:text-textWhite1"
            >
              <option value="ku">{{ $t("kurdish") }}</option>
              <option value="ar">{{ $t("arabic") }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      :class="[
        'md:flex md:gap-3 items-start',
        uiDesignMode === 'green' && $store.getters['isRtl'] ? 'md:flex-row-reverse' : '',
      ]"
    >
      <div
        v-if="showProductPanel"
        :class="[
          'w-full flex flex-col py-1 mx-auto md:mx-0 transition-all duration-300',
          uiDesignMode === 'green'
            ? 'md:w-8/12'
            : isShowItems
              ? 'md:w-5/12'
              : 'md:w-4/12',
        ]"
      >
        <!-- Categories Dropdown -->
        <h2
          id="accordion-color-heading-categories"
          :class="
            isShowCategories ? 'border-primaryColor text-blue-600' : ''
          "
        >
          <button
            type="button"
            :class="[
              'flex items-center justify-between w-full font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border',
              uiDesignMode === 'green'
                ? 'bg-green-50 dark:bg-bgFullDark border-green-300 dark:border-green-700 hover:border-green-500 px-3 py-1.5 text-sm rounded-lg'
                : 'bg-slate-100 dark:bg-bgFullDark border-gray-300 dark:border-gray-600 hover:border-primaryColor px-4 py-2',
            ]"
            @click="isShowCategories = !isShowCategories"
          >
            <span>{{ $t("items_categorys") }}</span>
            <svg
              data-accordion-icon
              class="w-3 h-3 rotate-180 shrink-0"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5 5 1 1 5"
              />
            </svg>
          </button>
        </h2>
        <div
          :class="[
            'flex flex-wrap rounded-2xl w-full overflow-x-auto mb-2',
            uiDesignMode === 'green'
              ? 'bg-white dark:bg-bgMeduimDark border border-green-200 dark:border-green-800 p-2 gap-1'
              : 'bg-white dark:bg-bgMeduimDark border border-gray-200 dark:border-gray-700 p-2 gap-1',
          ]"
          v-if="isShowCategories"
        >
          <button
            :class="
              (uiDesignMode === 'green'
                ? 'px-2 py-1 text-xs bg-green-50 dark:bg-bgFullDark text-textBlack2 dark:text-textWhite3 rounded-lg border '
                : 'px-2 py-1 text-xs bg-slate-50 dark:bg-bgFullDark text-textBlack2 dark:text-textWhite3 rounded-lg border ') +
              (this.$store.state.sale_pos.ca_id == ''
                ? 'border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            @click="this.$store.state.sale_pos.ca_id = ''"
          >
            {{ $t("all") }}
          </button>
          <button
            :class="
              (uiDesignMode === 'green'
                ? 'px-2 py-1 text-xs bg-green-50 dark:bg-bgFullDark text-textBlack2 dark:text-textWhite3 rounded-lg border '
                : 'px-2 py-1 text-xs bg-slate-50 dark:bg-bgFullDark text-textBlack2 dark:text-textWhite3 rounded-lg border ') +
              (this.$store.state.sale_pos.ca_id == category.ca_id
                ? 'border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            v-for="(category, index) in this.$store.state.item_category
              .categorys"
            :key="index"
            @click="this.$store.state.sale_pos.ca_id = category.ca_id"
          >
            {{ category.ca_name }}
          </button>
        </div>

        <!-- Items Grid -->
        <h2
          id="accordion-color-heading-items"
          :class="
            isShowItems ? 'border-primaryColor text-blue-600' : ''
          "
        >
          <button
            type="button"
            :class="[
              'flex items-center justify-between w-full px-4 py-2 font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border',
              uiDesignMode === 'green'
                ? 'bg-green-50 dark:bg-bgFullDark border-green-300 dark:border-green-700 hover:border-green-500 px-3 py-1.5 text-sm rounded-lg'
                : 'bg-slate-100 dark:bg-bgFullDark border-gray-300 dark:border-gray-600 hover:border-primaryColor',
            ]"
            @click="isShowItems = !isShowItems"
          >
            <span>{{ $t("itemss") }}</span>
            <svg
              data-accordion-icon
              class="w-3 h-3 rotate-180 shrink-0"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5 5 1 1 5"
              />
            </svg>
          </button>
        </h2>
        <div
          :class="[
            'grid',
            uiDesignMode === 'green'
              ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 pb-4'
              : compactItemCards
                ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 pb-8'
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 pb-8',
          ]"
          v-if="isShowItems"
        >
          <div v-if="uiDesignMode !== 'green'" class="col-span-full mb-2">
            <div class="relative w-full">
              <input
                :value="$store.state.sale_pos.searchPos"
                @input="handleSearchInput($event.target.value)"
                type="text"
                class="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-textBlack1 dark:border-gray-600 dark:bg-bgFullDark dark:text-white"
                :placeholder="$t('search_item')"
              />
              <i class="fa-solid fa-search pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primaryColor text-sm"></i>
            </div>
          </div>
          <!-- item 1-->
          <div
            :class="
              (uiDesignMode === 'green'
                ? 'flex flex-col p-1.5 rounded-lg items-center shadow-sm cursor-pointer transition hover:shadow-md min-h-[145px] '
                : 'flex flex-col p-1.5 rounded-lg items-center shadow-sm cursor-pointer transition hover:shadow-md min-h-[145px] ') +
              (uiDesignMode === 'green'
                ? 'bg-white dark:bg-bgMeduimDark border-green-300 dark:border-green-700 '
                : 'bg-white dark:bg-bgMeduimDark ') +
              (checkHowMuchItemInCard(item.i_id) != 0
                ? 'border border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            v-for="(item, index) in filterItemsPos"
            :key="index"
          >
            <div class="mb-1">
              <button
                type="button"
                @click="removeItem(item.i_id)"
                class="text-left mb-1 mx-1"
                v-if="checkHowMuchItemInCard(item.i_id) != 0"
              >
                <i
                  class="fa-solid fa-trash text-center p-1 w-6 h-6 text-xs bg-red-200 dark:bg-red-300 rounded-md text-red-500 mx-auto my-auto"
                ></i>
              </button>

              <a
                class="text-left mb-1 mx-1"
                :href="'item-detail/' + item.i_id"
                target="_blank"
              >
                <i
                  class="fa-solid fa-eye text-center p-1 w-6 h-6 text-xs bg-blue-200 dark:bg-blue-300 rounded-md text-blue-500 mx-auto my-auto"
                ></i>
              </a>
            </div>
            <img
              :src="file_url + item.i_image"
              alt=""
              :class="
                uiDesignMode === 'green'
                  ? 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
                  : compactItemCards
                    ? 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
                    : 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
              "
              @click="addItemToInvoice(item)"
              v-if="item.i_image != '' && item.i_image != null"
            />

            <img
              src="../../assets/image/image_not_found.png"
              alt=""
              :class="
                uiDesignMode === 'green'
                  ? 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
                  : compactItemCards
                    ? 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
                    : 'mb-1 rounded-lg h-14 w-14 object-cover cursor-pointer'
              "
              @click="addItemToInvoice(item)"
              v-if="item.i_image == '' || item.i_image == null"
            />

            <p
              :class="uiDesignMode === 'green' ? 'text-textBlack1 dark:text-textWhite1 text-xs text-center cursor-pointer line-clamp-2 min-h-[30px]' : 'text-textBlack1 dark:text-textWhite1 text-xs text-center cursor-pointer line-clamp-2 min-h-[30px]'"
              @click="addItemToInvoice(item)"
            >
              {{ item.i_name }}
            </p>

            <p
              :class="uiDesignMode === 'green' ? 'text-textBlack2 dark:text-textWhite2 cursor-pointer text-xs font-semibold' : 'text-textBlack2 dark:text-textWhite2 cursor-pointer text-xs font-semibold'"
              @click="addItemToInvoice(item)"
            >
              {{ formatNumber(item.i_sale_price) }}
              {{ this.$t(item.currency.cur_name) }}
            </p>
            <p
              :class="uiDesignMode === 'green' ? 'text-textBlack1 dark:text-textWhite1 text-[11px] text-center cursor-pointer' : 'text-textBlack1 dark:text-textWhite1 text-[11px] text-center cursor-pointer'"
              @click="addItemToInvoice(item)"
            >
              {{ $t("remain") }} {{ item.i_quantity }}
            </p>
            <span
              v-show="checkHowMuchItemInCard(item.i_id) != 0"
              :class="uiDesignMode === 'green' ? 'bg-green-600 px-2 py-1 text-white rounded-full text-[10px] mt-1' : 'bg-green-600 px-2 py-1 text-white rounded-full text-[10px] mt-1'"
              @click="addItemToInvoice(item)"
            >
              {{ checkHowMuchItemInCard(item.i_id) }}
            </span>
          </div>
        </div>
        <center>
          <span
            v-show="isShowItems && filterItemsPos.length == 0 && isLoadingData == false"
            class="text-textBlack1 dark:text-white"
          >
            {{ $t("no_item_found") }}
          </span>
        </center>
        <center>
          <img
            src="../../assets/loading.webp"
            style="width: 70px; height: 70px"
            v-show="isShowItems && isLoadingData"
          />
        </center>
      </div>

      <div
        :class="[
          'w-full my-2 py-3 rounded-2xl mb-24 md:mb-16 mx-auto md:mx-0 transition-all duration-300 shadow-sm border',
          uiDesignMode === 'green'
            ? 'bg-white dark:bg-bgMeduimDark border-green-200 dark:border-green-700/70'
            : 'bg-white dark:bg-bgMeduimDark border-gray-200/70 dark:border-gray-700/70',
          uiDesignMode === 'green'
            ? showProductPanel
              ? 'md:w-4/12 md:sticky md:top-16 self-start max-h-[calc(100vh-5rem)] overflow-y-auto'
              : 'md:w-full'
            : showProductPanel
              ? 'md:w-7/12'
              : 'md:w-full',
        ]"
      >
        <form action="">
          <!--sale extra information-->

          <div id="accordion-color">
            <h2
              id="accordion-color-heading-1"
              :class="isShowDetail ? 'border-primaryColor text-blue-600' : ''"
            >
              <button
                type="button"
                :class="[
                  'flex items-center justify-between w-full px-4 py-2 font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border',
                  uiDesignMode === 'green'
                    ? 'bg-green-50 dark:bg-bgFullDark border-green-300 dark:border-green-700 hover:border-green-500'
                    : 'bg-slate-100 dark:bg-bgFullDark border-gray-300 dark:border-gray-600 hover:border-primaryColor',
                ]"
                @click="isShowDetail = !isShowDetail"
              >
                <span
                  >{{ $t("detail") }}
                  {{ in_type == 1 ? $t("sale_tak") : $t("sale_jwmla") }}</span
                >
                <svg
                  data-accordion-icon
                  class="w-3 h-3 rotate-180 shrink-0"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5 5 1 1 5"
                  />
                </svg>
              </button>
            </h2>

            <!-- extra sale information-->
            <div
              id="accordion-color-body-1"
              aria-labelledby="accordion-color-heading-1"
              v-show="isShowDetail"
            >
              <div class="p-5 grid grid-cols-2 gap-x-3">
                <DropdownInput
                  v-model="bo_id"
                  :label="$t('box')"
                  :placeholder="$t('choose_box_that_money_get_in')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValueBoxes"
                  :isError="
                    checkRulesIsError(bo_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(bo_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/all-box"
                />
                <DropdownInput
                  v-model="st_id"
                  :label="$t('sale_type')"
                  :placeholder="$t('choose_sale_type')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValueSaleTypes"
                  :isError="
                    checkRulesIsError(st_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(st_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/add-sale-type"
                />
                <div class="mb-6">
                  <div class="flex">
                    <div class="w-full">
                      <DropdownInput
                        v-model="cu_id"
                        :label="$t('customer')"
                        :placeholder="$t('choose_customer')"
                        :items="convertToListIdValueCustomers"
                        :rules="rules_dropdown"
                        :isError="
                          checkRulesIsError(cu_id, 'add', this.rules_dropdown).isError
                        "
                        :errorMsg="
                          checkRulesIsError(cu_id, 'add', this.rules_dropdown).errorMsg
                        "
                        @change="getCustomerDebt"
                      />

                      <span
                        v-show="isShowDebts"
                        class="text-sm"
                        :class="Number(beforeDebtIQD) > 0 ? 'text-red-500' : 'text-green-500'"
                        >{{ $t("total_debt_dinar") }} :
                        {{ formatNumber(beforeDebtIQD) }}
                      </span>
                      <div class="w-2"></div>
                      <span
                        v-show="isShowDebts"
                        class="text-sm"
                        :class="Number(beforeDebtUSD) > 0 ? 'text-red-500' : 'text-green-500'"
                        >{{ $t("total_debt_dollar") }} :
                        {{ formatNumber(beforeDebtUSD) }}
                      </span>
                    </div>
                    <div class="w-2"></div>
                    <a
                      class="w-2/12 lg:w-1/12 text-white text-2xl my-8 bg-primaryColor p-1 rounded-lg hover:shadow-md text-center"
                      target="_blank"
                      href="/customer"
                    >
                      +
                    </a>
                  </div>
                </div>

                <DropdownInput
                  v-model="pl_id"
                  :label="$t('sale_platform')"
                  :placeholder="$t('choose_sale_platform')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValuePlatforms"
                  :isError="
                    checkRulesIsError(pl_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(pl_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/sale-platform"
                />
                <BaseInput
                  v-model="created_at"
                  type="date"
                  :label="$t('sale_date')"
                  :placeholder="$t('sale_date')"
                  :rules="no_rule"
                  :isError="
                    checkRulesIsError(created_at, 'add', this.no_rule).isError
                  "
                  :errorMsg="
                    checkRulesIsError(created_at, 'add', this.no_rule).errorMsg
                  "
                />
                <div>
                  <label
                    for="currency"
                    class="block mb-2 text-base font-medium text-textBlack1 dark:text-textWhite1"
                    >{{ $t("invoice_type_sale") }}</label
                  >
                  <div class="flex mx-4 mt-4 items-center gap-3 flex-wrap">
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-radio"
                        type="radio"
                        value="1"
                        checked="true"
                        v-model="in_type"
                        name="inline-radio-group"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="getPosData"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("sale_tak") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-2-radio"
                        type="radio"
                        value="2"
                        v-model="in_type"
                        @change="getPosData"
                        name="inline-radio-group"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        for="inline-2-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("sale_jwmla") }}</label
                      >
                    </div>
                  </div>
                </div>
                <TextAreaInput
                  v-model="in_note"
                  :label="$t('note')"
                  :placeholder="$t('note')"
                  :rules="no_rule"
                  :isError="
                    checkRulesIsError(in_note, 'add', this.no_rule).isError
                  "
                  :errorMsg="
                    checkRulesIsError(in_note, 'add', this.no_rule).errorMsg
                  "
                />
                <div>
                  <label
                    for="currency"
                    class="block mb-2 text-base font-medium text-textBlack1 dark:text-textWhite1"
                    >{{ $t("do_you_want_print_right_after_sale") }}</label
                  >
                  <div class="flex mx-4 mt-4">
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-radio11"
                        type="radio"
                        value="1"
                        checked="true"
                        v-model="isPrint"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="printStatus"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("cashier") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-3-radio11"
                        type="radio"
                        value="3"
                        v-model="isPrint"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="printStatus"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("a4") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-2-radio11"
                        type="radio"
                        value="2"
                        v-model="isPrint"
                        @change="printStatus"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        for="inline-2-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("no") }}</label
                      >
                    </div>
                  </div>
                </div>

                <div>
                  <div class="w-full">
                    <label
                      for="fast_customer"
                      class="block mb-2 text-sm font-medium text-textBlack1 dark:text-textWhite1"
                      >{{ $t("fast_customer") }}</label
                    >
                  </div>
                  <div class="flex">
                    <BaseInput
                      v-model="customer_name"
                      :label="$t('customer_name')"
                      :placeholder="$t('customer_name')"
                      :rules="no_rule"
                      :isError="
                        checkRulesIsError(customer_name, 'add', this.no_rule)
                          .isError
                      "
                      :errorMsg="
                        checkRulesIsError(customer_name, 'add', this.no_rule)
                          .errorMsg
                      "
                    />
                    <div class="w-2"></div>

                    <BaseInput
                      v-model="customer_phone_number"
                      :label="$t('customer_phone_number')"
                      :placeholder="$t('customer_phone_number')"
                      :rules="no_rule"
                      :isError="
                        checkRulesIsError(
                          customer_phone_number,
                          'add',
                          this.no_rule
                        ).isError
                      "
                      :errorMsg="
                        checkRulesIsError(
                          customer_phone_number,
                          'add',
                          this.no_rule
                        ).errorMsg
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Green POS order sidebar layout -->
          <div
            v-if="uiDesignMode === 'green'"
            :class="!showProductPanel ? 'mt-0.5 space-y-1.5' : 'mt-1 space-y-2'"
          >
            <div
              :class="[
                'sticky top-0 z-20 rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50/95 dark:bg-bgMeduimDark/95 backdrop-blur shadow-sm',
                !showProductPanel ? 'p-2' : 'p-2.5',
              ]"
            >
              <div
                :class="[
                  'rounded-lg border border-blue-200 dark:border-blue-700 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-bgLightDark dark:to-bgMeduimDark',
                  !showProductPanel ? 'p-1.5 mb-1.5' : 'p-2 mb-2',
                ]"
              >
                <span class="text-xs md:text-sm font-semibold text-textBlack1 dark:text-textWhite1 block">
                  {{ $t("total_price") }}
                </span>
                <div class="flex items-end justify-between mt-1">
                  <span :class="!showProductPanel ? 'text-2xl md:text-3xl leading-none font-extrabold text-blue-700 dark:text-blue-300 tracking-tight' : 'text-3xl md:text-4xl leading-none font-extrabold text-blue-700 dark:text-blue-300 tracking-tight'">
                    {{ formatPriceByCurrency(sumPrice) }}
                  </span>
                  <span class="text-sm md:text-base font-bold text-blue-700 dark:text-blue-300">
                    <span v-show="cur_id == 1">{{ $t("dinar") }}</span>
                    <span v-show="cur_id == 2">{{ $t("dollar") }}</span>
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium whitespace-nowrap">
                  {{ $t("money_amount_received") }}
                </span>
                <input
                  type="text"
                  inputmode="decimal"
                  :class="!showProductPanel ? 'bg-slate-50 border border-gray-300 text-textBlack1 text-sm rounded-lg block w-full h-8 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:text-white' : 'bg-slate-50 border border-gray-300 text-textBlack1 text-sm rounded-lg block w-full h-9 px-2.5 dark:bg-bgFullDark dark:border-gray-600 dark:text-white'"
                  :value="String(in_paid ?? 0)"
                  @input="onRootDecimalLocaleInput($event, 'in_paid')"
                />
              </div>
              <div
                v-if="sumPrice - in_paid > 0"
                class="mt-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 flex items-center justify-between"
              >
                <span class="text-sm font-semibold text-red-700 dark:text-red-300">
                  {{ $t("debt") }}
                </span>
                <span class="text-lg font-extrabold text-red-700 dark:text-red-300">
                  {{ formatNumber(sumPrice - in_paid) }}
                  <span class="text-xs font-bold" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                  <span class="text-xs font-bold" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                </span>
              </div>
              <div class="flex items-center gap-2 mt-2 flex-wrap">
                <button
                  type="button"
                  :class="!showProductPanel ? 'h-8 w-8 rounded-md bg-blue-600 text-white text-xs' : 'h-9 w-9 rounded-md bg-blue-600 text-white text-sm'"
                  @click="showModal(current)"
                >
                  <i class="fa-solid fa-calculator"></i>
                </button>
                <button
                  type="button"
                  :class="!showProductPanel ? 'h-8 w-8 rounded-md bg-red-500 text-white text-xs' : 'h-9 w-9 rounded-md bg-red-500 text-white text-sm'"
                  @click="clearInvoice(true)"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
                <button
                  type="button"
                  :class="!showProductPanel ? 'h-8 min-w-[84px] rounded-md bg-blue-600 text-white font-bold text-xs px-2' : 'h-9 min-w-[95px] rounded-md bg-blue-600 text-white font-bold text-sm px-2'"
                  @click="addSaleItem"
                >
                  <span v-if="!isBtnLoading">{{ $t("sale") }}</span>
                  <span v-else>{{ $t("wait") }}</span>
                </button>
                <select
                  v-model="printInvoiceLanguage"
                  :class="!showProductPanel ? 'h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-bgFullDark px-2 text-[11px] text-textBlack1 dark:text-textWhite1' : 'h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-bgFullDark px-2 text-xs text-textBlack1 dark:text-textWhite1'"
                >
                  <option value="ku">{{ $t("kurdish") }}</option>
                  <option value="ar">{{ $t("arabic") }}</option>
                </select>
              </div>
              <div v-if="!showProductPanel" class="grid grid-cols-3 gap-1.5 mt-1.5">
                <button v-for="n in ['7','8','9','4','5','6','1','2','3','.','0','00']" :key="`kp-${n}`" type="button" class="h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-bgFullDark text-xs font-semibold text-blue-700 dark:text-blue-300">
                  {{ n }}
                </button>
              </div>
            </div>

            <div
              :class="[
                'rounded-xl border border-blue-200 dark:border-blue-700 p-1.5 bg-white dark:bg-bgLightDark overflow-y-auto',
                !showProductPanel ? 'max-h-[calc(100vh-150px)]' : 'max-h-[320px]',
              ]"
            >
              <div
                :class="!showProductPanel ? 'text-[11px] text-textBlack1 dark:text-textWhite1 mb-1 font-semibold' : 'text-sm text-textBlack1 dark:text-textWhite1 mb-2 font-semibold'"
              >
                {{ $t("itemss") }} ({{ getSoldListByCurrentCurrency.length }})
              </div>
              <div
                v-if="getSoldListByCurrentCurrency.length === 0"
                class="text-sm text-gray-500 dark:text-gray-400 py-4 text-center"
              >
                {{ $t("no_item_in_invoice") }}
              </div>

              <div v-else-if="!showProductPanel" class="rounded-md border border-blue-200 dark:border-blue-700 overflow-hidden">
                <table class="w-full text-[11px] text-right">
                  <thead class="bg-blue-100 dark:bg-bgFullDark text-gray-700 dark:text-gray-300">
                    <tr>
                      <th class="px-1.5 py-1">{{ $t("item_name") }}</th>
                      <th class="px-1.5 py-1">{{ $t("quantity") }}</th>
                      <th class="px-1.5 py-1">{{ $t("price") }}</th>
                      <th class="px-1.5 py-1">{{ $t("final_price") }}</th>
                      <th class="px-1.5 py-1">{{ $t("actions") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in getSoldListByCurrentCurrency"
                      :key="`green-cart-compact-${index}`"
                      class="border-b border-blue-100 dark:border-gray-700"
                    >
                      <td class="px-1.5 py-1.5 align-middle">
                        <p class="truncate max-w-[130px] font-semibold text-textBlack1 dark:text-textWhite1">{{ item.i_name }}</p>
                        <p class="text-[10px] text-gray-500">{{ item.i_barcode }}</p>
                      </td>
                      <td class="px-1.5 py-1.5 align-middle">
                        <div class="flex items-center gap-1">
                          <button
                            type="button"
                            class="w-5 h-5 rounded bg-blue-50 dark:bg-gray-700 text-[10px]"
                            @click="updateInvoice(item, item.si_quantity - 1)"
                          >
                            -
                          </button>
                          <input
                            type="text"
                            inputmode="numeric"
                            class="w-9 h-6 text-center bg-slate-50 border border-gray-300 text-[10px] rounded dark:bg-bgFullDark dark:border-gray-600"
                            :value="String(item.si_quantity ?? 1)"
                            @input="onGreenLineQtyLocaleInput($event, item)"
                          />
                          <button
                            type="button"
                            class="w-5 h-5 rounded bg-blue-50 dark:bg-gray-700 text-[10px]"
                            @click="updateInvoice(item, Number(item.si_quantity) + 1)"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td class="px-1 py-1.5 align-middle">
                        <input
                          type="text"
                          inputmode="decimal"
                          class="w-14 h-6 text-center bg-slate-50 border border-gray-300 text-[10px] rounded dark:bg-bgFullDark dark:border-gray-600"
                          :value="lineSalePriceInputValue(item)"
                          @input="onLineSalePriceLocaleInput($event, item, { recalcDiscount: true })"
                        />
                      </td>
                      <td class="px-1.5 py-1.5 align-middle font-bold text-[11px]">
                        {{ formatNumber(item.si_quantity * getPriceByUnit(item) - item.si_discount) }}
                      </td>
                      <td class="px-1.5 py-1.5 align-middle">
                        <button type="button" @click="removeItem(item.i_id)">
                          <i class="fa-solid fa-trash text-center p-1 w-5 h-5 text-[10px] bg-red-200 dark:bg-red-300 rounded text-red-500"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-else
                v-for="(item, index) in getSoldListByCurrentCurrency"
                :key="`green-cart-${index}`"
                class="rounded-lg border border-gray-200 dark:border-gray-700 p-2 mb-2 bg-white dark:bg-bgMeduimDark"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold text-textBlack1 dark:text-textWhite1 truncate">
                      {{ item.i_name }}
                    </p>
                    <p class="text-xs text-gray-500">{{ item.i_barcode }}</p>
                  </div>
                  <button type="button" @click="removeItem(item.i_id)">
                    <i
                      class="fa-solid fa-trash text-center p-1 w-6 h-6 text-xs bg-red-200 dark:bg-red-300 rounded-md text-red-500"
                    ></i>
                  </button>
                </div>
                <div class="flex items-center justify-between mt-2">
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="w-7 h-7 rounded bg-gray-100 dark:bg-gray-700"
                      @click="updateInvoice(item, item.si_quantity - 1)"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputmode="numeric"
                      class="w-14 h-8 text-center bg-slate-50 border border-gray-300 text-sm rounded-lg dark:bg-bgFullDark dark:border-gray-600"
                      :value="String(item.si_quantity ?? 1)"
                      @input="onGreenLineQtyLocaleInput($event, item)"
                    />
                    <button
                      type="button"
                      class="w-7 h-7 rounded bg-gray-100 dark:bg-gray-700"
                      @click="updateInvoice(item, Number(item.si_quantity) + 1)"
                    >
                      +
                    </button>
                  </div>
                  <p class="text-base font-bold text-textBlack1 dark:text-textWhite1">
                    {{ formatNumber(item.si_quantity * getPriceByUnit(item) - item.si_discount) }}
                  </p>
                </div>
                <div class="mt-2 flex items-center justify-between gap-2">
                  <label class="text-xs text-gray-600 dark:text-gray-400 shrink-0">{{ $t("price") }}</label>
                  <div class="flex min-w-0 flex-1 items-center justify-end gap-1">
                    <input
                      type="text"
                      inputmode="decimal"
                      class="h-8 w-full max-w-[7rem] rounded-lg border border-gray-300 bg-slate-50 px-2 text-center text-sm text-textBlack1 dark:border-gray-600 dark:bg-bgFullDark dark:text-white"
                      :value="lineSalePriceInputValue(item)"
                      @input="onLineSalePriceLocaleInput($event, item, { recalcDiscount: true })"
                    />
                    <span class="text-[10px] text-gray-500 shrink-0" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                    <span class="text-[10px] text-gray-500 shrink-0" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="showProductPanel" class="grid grid-cols-1 gap-2">
              <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark p-2">
                <label class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {{ $t("total_discount") }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    inputmode="decimal"
                    class="bg-slate-50 border border-gray-300 text-textBlack1 text-sm rounded-lg block w-full h-9 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:text-white"
                    :value="totalDiscountInputDisplay(total_discount)"
                    @input="onTotalDiscountLocaleInput"
                  />
                  <span class="text-xs" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                  <span class="text-xs" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                </div>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark p-2">
                <label class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {{ $t("percentage_discount") }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    inputmode="decimal"
                    class="bg-slate-50 border border-gray-300 text-textBlack1 text-sm rounded-lg block w-full h-9 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:text-white"
                    :value="String(percentage_discount ?? 0)"
                    @input="onPercentageDiscountLocaleInput"
                  />
                  <span class="text-xs">%</span>
                </div>
              </div>
            </div>

            <div v-if="showProductPanel" class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark p-3 text-sm">
              <div class="flex justify-between mb-1">
                <span>{{ $t("total_without_discount") }}</span>
                <span>{{ formatPriceByCurrency(sumPriceWithoutDiscount) }}</span>
              </div>
              <div class="flex justify-between mb-1">
                <span>{{ $t("discount_amount") }}</span>
                <span>{{ formatPriceByCurrency(sumDiscount) }}</span>
              </div>
              <div class="flex justify-between font-bold text-base">
                <span>{{ $t("total_price") }}</span>
                <span>{{ formatPriceByCurrency(sumPrice) }}</span>
              </div>
            </div>
          </div>

          <!--table-->
          <div
            v-else
            class="relative overflow-x-auto mt-2 mb-2 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark max-h-[calc(100vh-285px)]"
          >
            <div class="sticky top-0 z-0 bg-white/95 dark:bg-bgMeduimDark/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 p-2">
              <form @submit="enterBarcode" class="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-2">
                <button
                  id="boxTypeClassicInCart"
                  type="button"
                  class="inline-flex h-10 shrink-0 items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white px-2 text-xs font-medium text-textBlack1 dark:border-gray-600 dark:bg-bgFullDark dark:text-white sm:w-[5.25rem] sm:min-w-[5.25rem] sm:max-w-[6rem]"
                  @click="changeCurrencyType"
                >
                  <i class="fa-solid fa-arrows-rotate text-primaryColor text-[10px] shrink-0"></i>
                  <span class="truncate" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                  <span class="truncate" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                </button>
                <div class="relative min-w-0 flex-1">
                  <input
                    ref="barcodeInput"
                    v-model="barcode"
                    type="text"
                    class="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-textBlack1 dark:border-gray-600 dark:bg-bgFullDark dark:text-white"
                    :placeholder="$t('barcode')"
                  />
                  <i class="fa-solid fa-barcode pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primaryColor text-sm"></i>
                </div>
              </form>
            </div>
            <!--in ltr change (text-right to text-left)-->
            <table class="w-full text-right text-xs text-gray-600 dark:text-gray-300">
              <thead
                :class="[
                  'text-[11px] text-gray-700 dark:text-gray-300 uppercase border-b border-gray-200 dark:border-gray-700',
                  uiDesignMode === 'green' ? 'bg-green-50 dark:bg-bgFullDark' : 'bg-slate-100 dark:bg-bgFullDark',
                ]"
              >
                <tr>
                  <th scope="col" class="px-2 py-1.5">{{ $t("item_name") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("barcode") }}</th>
                  <!--   <th scope="col" class="px-2 py-1">{{ $t("cost") }}</th>-->
                  <th scope="col" class="px-2 py-1.5">{{ $t("price") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("type") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("quantity") }}</th>
                  <th v-if="showCartBonusColumn" scope="col" class="px-2 py-1.5">{{ $t("bonus") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("unit") }}</th>
                  <th scope="col" class="px-2 py-1.5" v-if="showCartPackagesColumn && hasItemsWithPackages">{{ $t("packages") }}</th>
                  <th v-if="showCartDiscountColumn" scope="col" class="px-2 py-1.5">{{ $t("discount") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("final_price") }}</th>
                  <th scope="col" class="px-2 py-1.5">{{ $t("actions") }}</th>
                </tr>
              </thead>
              <tbody class="text-xs">
                <tr
                  class="bg-white text-textBlack border-b border-gray-100 dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700 hover:bg-blue-50/40 dark:hover:bg-bgFullDark transition-colors"
                  v-for="(item, index) in getSoldListByCurrentCurrency"
                  :key="index"
                >
                  <td class="px-2 py-1.5 cursor-pointer font-medium"  @click="showPriceHistory(item)">
                    {{ item.i_name }}
                  </td>
                  <td class="px-2 py-1.5">
                    {{ item.i_barcode }}
                  </td>
                  <!--    <td class="px-2 py-2">
                    <select
                      class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      v-model="item.pui_id"
                    >
                      <option
                        v-for="(item1, index) in item.purchase_items"
                        :key="index"
                        :value="item1.pui_id"
                      >
                        {{ item1.pui_id }}
                      </option>
                    </select>
                  </td>
                   -->
                  <td class="px-2 py-1.5 relative">
                    <input
                      type="text"
                      inputmode="decimal"
                      class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block h-7 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      :value="lineSalePriceInputValue(item)"
                      @input="onLineSalePriceLocaleInput($event, item, { recalcDiscount: true })"
                    />
                  </td>
                  <td class="px-2 py-1.5">
                    <select
                      class="bg-slate-50 border border-gray-300 text-textBlack1 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block h-7 px-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:text-white"
                      v-model.number="item.si_is_package"
                      @change="onItemTypeChange(item)"
                    >
                      <option :value="0">{{ $t("piece") }}</option>
                      <option :value="1" :disabled="!(Number(item.i_package) > 0 && Number(item.i_package_price) > 0)">
                        {{ $t("carton") }}
                      </option>
                    </select>
                  </td>
                  <td class="px-2 py-1.5">
                    <input
                      type="text"
                      inputmode="numeric"
                      :ref="`quantity-input-${item.i_id}`"
                      class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-14 h-7 px-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      :value="String(item.si_quantity ?? 0)"
                      data-qty-input="true"
                      @input="onClassicLineQtyLocaleInput($event, item)"
                    />
                  </td>
                  <td v-if="showCartBonusColumn" class="px-2 py-1.5">
                    <input
                      type="text"
                      inputmode="numeric"
                      class="bg-green-50 border border-green-300 text-textBlack1 placeholder-gray-400 text-xs rounded-lg focus:ring-green-500 focus:border-green-500 block w-14 h-7 px-1.5 dark:bg-bgFullDark dark:border-green-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500"
                      :value="String(item.si_bonus_quantity ?? 0)"
                      placeholder="0"
                      :title="$t('bonus_gift_items')"
                      @input="onLineBonusQtyLocaleInput($event, item)"
                    />
                  </td>
                  <td class="px-2 py-1.5">
                    {{ getUnit(item) }}
                  </td>
                  <td class="px-2 py-1.5" v-if="showCartPackagesColumn && hasItemsWithPackages">
                    <div v-if="item.i_packages && item.i_packages.length > 0" class="space-y-1">
                      <div
                        v-for="(pkg, pkgIndex) in item.i_packages"
                        :key="pkgIndex"
                        class="text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        @click="setQuantityFromPackage(item, pkg.quantity, pkg.price)"
                        :title="$t('click_to_set_quantity')"
                      >
                        {{ pkg.name }}: {{ pkg.quantity }}+ = {{ formatNumber(pkg.price) }}
                      </div>
                    </div>
                    <span v-else class="text-gray-400 text-xs">
                      {{ $t("no_packages") }}
                    </span>
                  </td>
                  <td v-if="showCartDiscountColumn" class="px-2 py-1.5">
                    <input
                      type="text"
                      inputmode="decimal"
                      class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block h-7 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      :value="String(item.si_discount ?? 0)"
                      @input="onLineDiscountLocaleInput($event, item)"
                    />
                  </td>
                  <td class="px-2 py-1.5 relative">
                    <div 
                      class="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                      @click="showPriceHistory(item)"
                    >
                      {{
                        formatNumber(
                          item.si_quantity * getPriceByUnit(item) -
                            item.si_discount
                        )
                      }}
                      <span v-if="item.si_bonus_quantity > 0" class="text-green-600 dark:text-green-400 text-xs block">
                        +{{ item.si_bonus_quantity }} {{ $t("free") }}
                      </span>
                      <span class="mx-1" v-show="cur_id == 1">{{
                        $t("dinar")
                      }}</span>
                      <span class="mx-1" v-show="cur_id == 2">{{
                        $t("dollar")
                      }}</span>
                    </div>
                  </td>
                  <td class="px-2 py-1.5">
                    <button type="button" @click="removeItem(item.i_id)">
                      <i
                        class="fa-solid fa-trash text-center p-1 w-6 h-6 text-xs bg-red-200 dark:bg-red-300 rounded-md text-red-500 mx-auto my-auto"
                      ></i>
                    </button>
                  </td>
                </tr>
                <tr
                  class="bg-slate-50/70 text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700"
                  v-show="sold_list.length != 0"
                >
                  <td :colspan="classicCartColspan" class="px-3 py-2">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <div class="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {{ $t("total_without_discount") }}:
                        </div>
                        {{ formatPriceByCurrency(sumPriceWithoutDiscount) }}
                        <span class="mx-1" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                        <span class="mx-1" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                      </div>
                      <div>
                        <div class="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {{ $t("discount_amount") }}:
                        </div>
                        {{ formatPriceByCurrency(sumDiscount) }}
                        <span class="mx-1" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                        <span class="mx-1" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                      </div>
                      <div>
                        <div class="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {{ $t("total_price") }}:
                        </div>
                        <span class="font-semibold">{{ formatPriceByCurrency(sumPrice) }}</span>
                        <span class="mx-1" v-show="cur_id == 1">{{ $t("dinar") }}</span>
                        <span class="mx-1" v-show="cur_id == 2">{{ $t("dollar") }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr
                  class="bg-slate-50/70 text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700"
                  v-show="sold_list.length != 0"
                >
                  <td :colspan="classicCartColspan" class="px-3 py-2">
                    <div class="flex flex-col gap-2 min-w-[230px]">
                      <div
                        class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark p-2"
                      >
                        <label
                          class="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block"
                        >
                          {{ $t("total_discount") }}
                        </label>
                        <div class="flex items-center gap-2">
                          <input
                            type="text"
                            inputmode="decimal"
                            class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full h-9 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            :value="totalDiscountInputDisplay(total_discount)"
                            @input="onTotalDiscountLocaleInput"
                          />
                          <span class="text-xs whitespace-nowrap" v-show="cur_id == 1">{{
                            $t("dinar")
                          }}</span>
                          <span class="text-xs whitespace-nowrap" v-show="cur_id == 2">{{
                            $t("dollar")
                          }}</span>
                        </div>
                      </div>
                      <div
                        class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-bgMeduimDark p-2"
                      >
                        <label
                          class="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block"
                        >
                          {{ $t("percentage_discount") }}
                        </label>
                        <div class="flex items-center gap-2">
                          <input
                            type="text"
                            inputmode="decimal"
                            class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full h-9 px-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            :value="String(percentage_discount ?? 0)"
                            @input="onPercentageDiscountLocaleInput"
                          />
                          <span class="text-xs">%</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <center>
              <span
                class="text-center text-textBlack1 dark:text-white"
                v-show="sold_list.length == 0"
                >{{ $t("no_item_in_invoice") }}</span
              >
            </center>
          </div>

        </form>
      </div>
    </div>

    <div
      v-if="uiDesignMode !== 'green'"
      class="flex flex-col lg:flex-row justify-between gap-3 lg:gap-4 items-start lg:items-center px-3 md:px-6 py-3 left-0 right-0 fixed bottom-0 border-t border-gray-200 dark:border-gray-700 rounded-t-2xl z-50 shadow-2xl bg-white dark:bg-bgMeduimDark"
    >
      <div class="flex flex-wrap items-center gap-3 lg:gap-6 w-full lg:w-auto">
        <p class="text-sm md:text-base text-textBlack1 dark:text-white">
          {{ $t("total_items_price") }} :
          <span class="text-xl md:text-2xl font-bold text-primaryColor">{{
            formatPriceByCurrency(sumPrice)
          }}</span>
          <span class="mx-1 font-bold text-lg" v-show="cur_id == 1">{{
            $t("dinar")
          }}</span>
          <span class="mx-1 font-bold text-lg" v-show="cur_id == 2">{{
            $t("dollar")
          }}</span>
        </p>

        <div class="flex items-center gap-2">
          <span
            class="text-sm md:text-base font-medium text-textBlack1 dark:text-textWhite1"
            >{{ $t("money_amount_received") }}:</span
          >
          <input
            type="text"
            inputmode="decimal"
            class="bg-slate-50 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 w-24 md:w-40 h-10 px-3 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            required
            :value="String(in_paid ?? 0)"
            @input="onRootDecimalLocaleInput($event, 'in_paid')"
          />
        </div>

        <p class="text-sm md:text-base text-textBlack1 dark:text-white">
          {{ $t("debt") }}:
          <span class="text-xl md:text-2xl font-bold text-red-500">{{
            formatNumber(sumPrice - in_paid)
          }}</span>
        </p>
      </div>

      <div class="flex items-center gap-2 lg:gap-3 w-full lg:w-auto justify-end flex-wrap">
        <div class="flex items-center gap-2">
          <button
            data-modal-target="defaultModal"
            data-modal-toggle="defaultModal"
            class="text-white text-lg bg-blue-500 w-12 h-10 rounded-xl items-center hover:shadow-md transition"
            @click="showModal(item)"
          >
            <i class="fa-solid fa-calculator"></i>
          </button>
          <button
            data-modal-target="defaultModal"
            data-modal-toggle="defaultModal"
            class="text-white text-lg bg-red-500 w-12 h-10 rounded-xl items-center hover:shadow-md transition"
            @click="clearInvoice(true)"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
        <button
          class="text-white text-base md:text-lg bg-primaryColor min-w-[120px] md:min-w-[150px] h-10 rounded-xl items-center hover:shadow-md transition px-4"
          @click="addSaleItem"
        >
          <div v-if="!isBtnLoading">{{ $t("sale") }}</div>

          <div
            class="flex items-center hover:shadow-mdtext-white flex justify-center items-center hover:shadow-md"
            role="status"
            v-else
          >
            <svg
              width="20"
              height="20"
              fill="currentColor"
              class="mr-2 animate-spin"
              viewBox="0 0 1792 1792"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M526 1394q0 53-37.5 90.5t-90.5 37.5q-52 0-90-38t-38-90q0-53 37.5-90.5t90.5-37.5 90.5 37.5 37.5 90.5zm498 206q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-704-704q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm1202 498q0 52-38 90t-90 38q-53 0-90.5-37.5t-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-964-996q0 66-47 113t-113 47-113-47-47-113 47-113 113-47 113 47 47 113zm1170 498q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-640-704q0 80-56 136t-136 56-136-56-56-136 56-136 136-56 136 56 56 136zm530 206q0 93-66 158.5t-158 65.5q-93 0-158.5-65.5t-65.5-158.5q0-92 65.5-158t158.5-66q92 0 158 66t66 158z"
              ></path>
            </svg>
            <span class="mx-1">{{ $t("wait") }}</span>
          </div>
        </button>
        <select
          v-model="printInvoiceLanguage"
          class="h-10 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-bgFullDark px-3 text-sm text-textBlack1 dark:text-textWhite1"
        >
          <option value="ku">{{ $t("kurdish") }}</option>
          <option value="ar">{{ $t("arabic") }}</option>
        </select>
      </div>
    </div>
  </div>
  <confirm-dialogue ref="confirmDialogue"></confirm-dialogue>
  <ResultDialog ref="ResultDialog"></ResultDialog>
  <DynamicModalAction ref="actionModal"
    ><div class="relative w-full max-w-2xl max-h-full">
      <!-- Modal content -->
      <div class="relative">
        <!-- Modal header -->
        <div class="flex items-start justify-between p-4">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ $t("change_money_counter") }}
          </h3>
          <i
            class="fa-solid fa-trash text-red-500"
            @click="recivedMoney = 0"
          ></i>
        </div>
        <!-- Modal body -->
        <div class="p-2 mb-14 md:mb-80 text-textBlack1 dark:text-white">
          <p>{{ $t("1_dollar_price") }} = {{ formatNumber(getDolarPrice) }}</p>
          <div class="md:flex w-full mt-2 rounded-lg">
            <div
              class="w-11/12 md:w-7/12 h-20 grid grid-cols-2 gap-x-2 gap-y-4 mx-auto"
            >
              <div
                class="flex flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 1)"
              >
                <img
                  src="../../assets/image/1_dolar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg curser-pointer"
                />
                <p class="text-center my-auto curser-pointer">
                  {{ $t("1_dollar") }}
                </p>
              </div>
              <div
                class="flex flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 5)"
              >
                <img
                  src="../../assets/image/5_dolar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("5_dollar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 50)"
              >
                <img
                  src="../../assets/image/50dollar.jpeg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("50_dollar") }}</p>
              </div>
              <div
                class="flex m-1 flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 100)"
              >
                <img
                  src="../../assets/image/100dollar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("100_dollar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 1000)"
              >
                <img
                  src="../../assets/image/1_iqd.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("1_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 5000)"
              >
                <img
                  src="../../assets/image/5dinnar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("5_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 10000)"
              >
                <img
                  src="../../assets/image/10dinnar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("10_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 25000)"
              >
                <img
                  src="../../assets/image/25dinnar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("25_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 50000)"
              >
                <img
                  src="../../assets/image/50dinnar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("50_thousand_dinar") }}</p>
              </div>
            </div>
            <div class="h-40 md:mt-0"></div>
            <div class="w-full md:w-5/12 h-20 mt-96 md:mt-0">
              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("items_price") }}</span>
                <input
                  type="text"
                  inputmode="decimal"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  :value="String(in_paid ?? 0)"
                  @input="onRootDecimalLocaleInput($event, 'in_paid')"
                />
              </div>

              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("money_amount_received") }}</span>
                <input
                  type="text"
                  inputmode="decimal"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  :value="String(recivedMoney ?? 0)"
                  @input="onRootDecimalLocaleInput($event, 'recivedMoney')"
                />
              </div>

              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("change_money") }} </span>
                <div class="w-2"></div>
                <input
                  type="text"
                  inputmode="decimal"
                  readonly
                  tabindex="-1"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  :value="String(getChangeMoney)"
                />
              </div>
            </div>
          </div>
        </div>
      </div></div
  ></DynamicModalAction>
  <div :class="classesPrintModal()">
    <!-- modal -->
    <div class="my_font bg-white rounded shadow-lg w-10/12 md:w-1/3">
      <!-- modal body -->
      <div
        class="p-3 bg-white my_font font-bold text-size-base text-black"
        id="invoice_print"
      >
        <div>
          <h6 class="text-center text-black font-bold text-size-lg">
            {{ settings.app_name }}
          </h6>
          <p class="my_font text-center font-bold text-size-base text-black">
            {{ getPrintableInvoiceHeader(settings.set_header_invoice) }}
          </p>
          <p class="my_font text-center font-bold text-size-base text-black">
            <i
              class="fas fa-phone text-size-base relative mx-2 text-pink text-center"
            ></i>
            {{
              settings.set_phone_number +
              (settings.set_phone_number2 != "" &&
              settings.set_phone_number2 != null
                ? " - " + settings.set_phone_number2
                : "")
            }}
          </p>
          <p class="my_font text-center font-bold text-size-base text-black">
            <i
              class="fas fa-map-marker-alt text-size-base relative mx-2 text-pink text-center"
            ></i>
            {{ settings.set_address }}
          </p>
        </div>
        <div class="w-full max-w-full shrink-0 md:w-full md:flex-0 bg-white">
          <div class="flex-auto pb-2 my_font">
            <div class="overflow-x-auto">
              <table
                class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
              >
                <tbody>
                  <tr>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("invoice_id") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ in_id }} 
                      
                    </td>
                  </tr>
                  <tr v-if="cu_id != ''">
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("customer_name") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ getCustomerDisplayLabel(cu_id, customer_name, customer_phone_number) }}
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("date") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      <div class="whitespace-normal">
                        {{ formatDate(created_at) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex-auto pb-2">
            <div class="overflow-x-auto">
              <table
                class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
              >
                <thead class="align-bottom text-center">
                  <tr>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      #
                    </th>

                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("item_name") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-pre-line leading-tight dark:border-white/40"
                    >
                      {{ $t("invoice_box_qty_print") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-pre-line leading-tight dark:border-white/40"
                    >
                      {{ $t("invoice_pcs_per_box_print") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("invoice_box_price") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("invoice_pieces_only") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("invoice_piece_price") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="totalBonusItems > 0"
                    >
                      {{ $t("bonus") }}
                    </th>

                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="showCartDiscountColumn && sumDiscount > 0"
                    >
                      {{ $t("discount") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("total_price") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    class="w-full"
                    v-for="(item, index) in getSoldListByCurrentCurrency"
                    :key="index"
                  >
                    <td
                      class="text-black -bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ index + 1 }}
                    </td>

                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-normal dark:border-white/40"
                    >
                      <div class="whitespace-normal">
                        {{ getInvoiceItemName(item) }}
                      </div>
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ getInvoiceBoxCountDisplay(item) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ getInvoicePiecesPerBoxDisplay(item) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ getInvoiceBoxPriceDisplay(item) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ formatNumber(getInvoicePiecesCount(item)) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ formatPriceByCurrency(getInvoicePiecePriceFromBox(item)) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="totalBonusItems > 0"
                    >
                      {{ item.si_bonus_quantity > 0 ? formatNumber(item.si_bonus_quantity) : '-' }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="showCartDiscountColumn && sumDiscount > 0"
                    >
                      {{ formatNumber(item.si_discount) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      colspan="1"
                    >
                      {{
                        formatNumber(
                          item.si_quantity * getPriceByUnit(item) -
                            item.si_discount
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div>
          <table
            class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
          >
            <tbody>
              <tr>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("items_price") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <p>
                    {{ formatPriceByCurrency(sumPriceWithoutDiscount) }}

                    <span
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 1"
                      >{{ $t("dinar") }}</span
                    >
                    <span 
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 2"
                      >{{ $t("dollar") }}</span
                    >
                  </p>
                </td>
              </tr>
              <tr v-if="sumDiscount > 0">
                <td
                  class="text-red-600 border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("discount") }} </b>
                </td>
                <td
                  class="text-red-600 text-size-base border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <p class="text-red-600 font-bold">
                    {{ formatNumber(sumDiscount) }}
                    <span
                      class="mx-1 my_font text-red-600 text-size-base"
                      v-show="cur_id == 1"
                      >{{ $t("dinar") }}</span
                    >
                    <span
                      class="mx-1 my_font text-red-600 text-size-base"
                      v-show="cur_id == 2"
                      >{{ $t("dollar") }}</span
                    >
                  </p>
                </td>
              </tr>

              <!--end tr-->
              <tr>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("final_price") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatPriceByCurrency(sumPrice) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
              <tr v-if="getInDue > 0">
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("debt") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatNumber(getInDue) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
              <tr v-if="getInDue > 0">
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("paid_money") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatNumber(in_paid) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="border-b w-full"></div>
      <div class="flex justify-start items-center w-full p-3 bg-white">
        <button
          class="mx-auto text-center bg-pink-600 hover:bg-pink-700 px-3 py-1 rounded text-white"
          @click="modalPrintID"
        >
          <svg
            aria-hidden="true"
            class="mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-pink-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            v-if="loading"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span v-if="!loading"> {{ $t("print") }}</span>
        </button>
      </div>
    </div>
  </div>
  <!-- Calculator Modal -->
  <CalculatorModal
    :isOpen="isCalculatorOpen"
    @close="closeCalculator"
    @open-calculator="openCalculator"
  />
 
  <teleport to="body">
    <div
      v-if="showWhatsAppModal"
      class="fixed inset-0 isolate z-[2147483646] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      :dir="$store.getters['isRtl'] ? 'rtl' : 'ltr'"
      @click.self="showWhatsAppModal = false"
    >
      <div
        :class="[
          'w-full max-w-xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-green-200 dark:border-green-800 bg-white dark:bg-bgMeduimDark shadow-2xl z-[2147483647]',
          $store.getters['isRtl'] ? 'text-right' : 'text-left',
        ]"
      >
        <div class="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 class="text-base sm:text-lg font-semibold text-textBlack1 dark:text-textWhite1">
              {{ $t("create_order") }}
            </h3>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ $t("Order message") }}
            </p>
          </div>
          <button
            type="button"
            class="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            @click="showWhatsAppModal = false"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="p-4 sm:p-5">
          <TextAreaInput
            v-model="message.text"
            :placeholder="$t('Order message')"
            rows="10"
          />
        </div>

        <div class="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <button
            type="button"
            class="h-10 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-textBlack1 dark:text-textWhite1 hover:bg-gray-50 dark:hover:bg-bgLightDark transition"
            @click="showWhatsAppModal = false"
          >
            {{ $t("close") }}
          </button>
          <button
            type="button"
            class="h-10 px-5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition"
            @click="handleWhatsAppMessageSubmit"
          >
            {{ $t("create_order") }}
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!-- Price History Dialog -->
  <div
    v-if="showPriceHistoryDialog"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="closePriceHistoryDialog"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
      @click.stop
    >
      <!-- Header -->
      <div class="flex justify-between items-center p-4 border-b">
        <h3 class="text-lg font-semibold text-gray-800">
          {{ $t("last_3_prices") }}
        </h3>
        <button
          @click="closePriceHistoryDialog"
          class="text-gray-400 hover:text-gray-600"
        >
          <i class="fa-solid fa-times text-xl"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4">
        <div v-if="selectedItemForHistory" class="mb-4">
          <h4 class="font-medium text-gray-700 mb-2">
            {{ selectedItemForHistory.i_name }}
          </h4>
          <p class="text-sm text-gray-500">
            {{ $t("customer") }}: {{ getCustomerName({ id: cu_id }) }}
          </p>
        </div>

        <!-- History List -->
        <div v-if="cu_id && selectedItemForHistory && customerItemHistory[selectedItemForHistory.i_id] && customerItemHistory[selectedItemForHistory.i_id].length > 0">
          <div v-for="(history, idx) in customerItemHistory[selectedItemForHistory.i_id]" :key="idx" class="mb-3 p-3 bg-gray-50 rounded-lg">
            <div class="flex justify-between items-center mb-1">
              <div class="text-sm text-gray-600">
                {{ formatDate(history.created_at) }}
              </div>
              <div class="text-sm font-bold text-blue-600">
                {{ formatNumber(history.si_sale_price) }} {{ $t(history.cur_name) }}
              </div>
            </div>
            <div class="text-xs text-gray-500">
              {{ $t("invoice") }}: {{ history.in_id }}
            </div>
          </div>
        </div>

        <!-- No History Message -->
        <div v-else class="text-center py-8 text-gray-500">
          <i class="fa-solid fa-history text-4xl mb-2"></i>
          <p>{{ $t("no_purchase_history") }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end p-4 border-t">
        <button
          @click="closePriceHistoryDialog"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t("close") }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import BaseInput from "../../components/Inputs/BaseInput.vue";
import TextAreaInput from "../../components/Inputs/TextAreaInput.vue";
import { formatDate } from "../../utils/functions";
import ConfirmDialogue from "../../components/Modals/ConfirmDialogue.vue";
import ResultDialog from "../../components/Modals/ResultDialog.vue";
import { FILE_URL } from "../../constants/config";
import { CURRENCYS } from "../../constants/config";
import { formatNumber } from "../../utils/functions";
import {
  sanitizeNumericInput,
  sanitizeIntegerInput,
  applyPercentageDecimalInput,
  applyRootDecimalLocaleInput,
  numericFromDecimalField,
  lineDecimalInputDisplay,
  processDecimalInputEvent,
} from "../../utils/localeDigits";
import { getEscapedInvoiceCustomerCityAddressLine } from "../../utils/invoiceCustomerAddress";
import DropdownInput from "../../components/Inputs/DropdownInput.vue";
import DynamicModalAction from "../../components/Modals/DynamicModalAction.vue";
import LoadingProgress from "../../components/loading-progress.vue";
import CalculatorModal from "../../components/Modals/CalculatorModal.vue";

//import html2pdf from "html2pdf.js";

export default {
  name: "ADD_SALE_TYPE",
  components: {
    BaseInput,
    ConfirmDialogue,
    ResultDialog,
    TextAreaInput,
    DropdownInput,
    DynamicModalAction,
    LoadingProgress,
    CalculatorModal,
  },
  data() {
    return {
      firstName: "",
      rules: [
        (value) => {
          if (value || value == 0) return true;

          return this.$t("you_must_fill_this_field");
        },
      ],
      rules_dropdown: [
        (value) => {
          if (value) return true;

          return this.$t("you_must_choose_an_option");
        },
      ],
      no_rule: [],
      inputType: "text",
      isSubmited: false,
      isSubmitedEdit: false,
      current: {},
      isShowDetail: false,
      file_url: FILE_URL,
      currencys: CURRENCYS,
      sold_list: [],
      barcode: "",
      in_paid: 0,
      in_price: 0,
      in_discount: 0,
      in_due: 0,
      total_discount: 0,
      percentage_discount: 0,
      cu_id: "",
      bo_id: "",
      cur_id: 1,
      st_id: "",
      in_main_currency_price: 0,
      pl_id: "",
      in_note: "",
      customer_name: "",
      customer_phone_number: "",
      created_at: "",
      tempItem: 0,
      recivedMoney: 0,
      changeMoney: 0,
      in_type: 1,
      isPrint: "2",
      printInvoiceLanguage: "",
      isModalPrint: false,
      in_id: "",
      settings: {},
      isShowItemsCategorys: true,
      isShowCategories: true,
      isShowItems: true,
      showUiCustomizer: false,
      showProductPanel: true,
      compactItemCards: false,
      showCartBonusColumn: false,
      showCartPackagesColumn: false,
      showCartDiscountColumn: true,
      isShowGreenRetailDetails: false,
      uiDesignMode: "classic",
      selectedGreenRowIndex: -1,
      selectedKeypadField: "in_paid",
      greenQuickSearchQuery: "",
      message: {
        text: "",
      },
      showWhatsAppModal: false,
      beforeDebtIQD: 0,
      beforeDebtUSD: 0,
      isShowDebts: false,
      invoiceIdList: [],
      showTooltipForItem: null,
      showPriceHistoryDialog: false,
      selectedItemForHistory: null,
      lastAddedItemId: null,
      barcodeRefocusTimer: null,
      isUiLayoutLoaded: false,
      isPosPreferencesLoaded: false,
    };
  },
  created() {
    this.loadPosPreferences();
    this.getSaleTypes();
    this.getBoxs();
    
    this.$store.dispatch("item_category/getCategory");
    this.getPosData();

    this.getCustomers();

    this.getPlatforms();

    this.$store.dispatch("navbar/getItems");

    // Calculate initial total discount after a short delay to ensure data is loaded
    this.$nextTick(() => {
      setTimeout(() => {
        this.calculateInitialTotalDiscount();
      }, 100);
    });

    //settings=localStorage.getItem("currentSettingWiseManager");
    this.settings = JSON.parse(
      localStorage.getItem("currentSettingWiseManager")
    );
    this.printInvoiceLanguage =
      localStorage.getItem("lang_wise_manager") || "ku";
    this.loadUiLayoutSettings();
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalQtyArrowKeys);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleGlobalQtyArrowKeys);
    if (this.barcodeRefocusTimer) {
      clearTimeout(this.barcodeRefocusTimer);
    }
  },
  watch: {
    cu_id() {
      this.getCustomerDebt();
      this.clearCustomerItemHistory();
    },
    // eslint-disable-next-line no-unused-vars
    getResultDialog(newValue, oldValue) {
      if (newValue) {
        this.$refs.ResultDialog.show({
          message: this.$store.state.sale_pos.resultMsg,
          okButton: this.$t("done"),
          isSuccess: this.$store.state.sale_pos.isSuccess,
        });

        // Reset the deleteSuccess state after showing the dialog
        this.$store.commit("sale_pos/SET_DELETE_SUCCESS", false);
      }
    },
    sold_list: {
      handler() {
        // Update total discount when sold_list changes
        this.calculateInitialTotalDiscount();
      },
      deep: true,
    },
    isCalculatorOpen: false,
    showProductPanel() {
      this.saveUiLayoutSettings();
    },
    isShowCategories() {
      this.saveUiLayoutSettings();
    },
    isShowItems() {
      this.saveUiLayoutSettings();
    },
    isShowDetail() {
      this.saveUiLayoutSettings();
    },
    compactItemCards() {
      this.saveUiLayoutSettings();
    },
    showCartBonusColumn() {
      this.saveUiLayoutSettings();
    },
    showCartPackagesColumn() {
      this.saveUiLayoutSettings();
    },
    showCartDiscountColumn() {
      this.saveUiLayoutSettings();
    },
    uiDesignMode() {
      if (this.uiDesignMode === "classic") {
        this.showProductPanel = true;
        this.isShowCategories = true;
        this.isShowItems = true;
        this.isShowDetail = true;
        this.compactItemCards = false;
      }
      this.saveUiLayoutSettings();
    },
    cur_id() {
      this.savePosPreferences();
    },
    isPrint() {
      this.savePosPreferences();
    },
  },

  computed: {
    getChangeMoney() {
      return this.recivedMoney - this.in_paid;
    },
    getInDue() {
      return this.sumPrice - this.in_paid;
    },
    getResultDialog() {
      return this.$store.state.sale_pos.isShowResultDialog;
    },
    customerItemHistory() {
      return this.$store.state.sale_pos.customerItemHistory;
    },
    getDolarPrice() {
      return this.$store.state.navbar.dp_price;
    },
    filterItems() {
      return this.$store.getters["sale_pos/filterItems"];
    },
    filterItemsPos() {
      return this.$store.getters["sale_pos/filterItemsPOS"];
    },
    greenQuickSearchResults() {
      const query = (this.greenQuickSearchQuery || "").trim().toLowerCase();
      if (!query) return [];

      return this.filterItemsPos
        .filter((item) => {
          const name = (item.i_name || "").toLowerCase();
          const barcode = String(item.i_barcode || "").toLowerCase();
          return name.includes(query) || barcode.includes(query);
        })
        .slice(0, 8);
    },
    showGreenQuickSearchResults() {
      return this.uiDesignMode === "green" && this.greenQuickSearchQuery.trim().length > 0;
    },
    isDark() {
      return this.$store.state.theme == "dark";
    },
    isLoadingData() {
      return this.$store.state.sale_pos.isLoadingData;
    },
    isBtnLoading() {
      return this.$store.state.sale_pos.isBtnLoading;
    },
    isBtnEditLoading() {
      return this.$store.state.sale_pos.isBtnEditLoading;
    },
    convertToListIdValueCategory() {
      var categorys = this.$store.state.item_category.categorys;
      var list = [];
      for (const item of categorys) {
        list.push({
          id: item.ca_id,
          value: item.ca_name,
        });
      }

      return list;
    },
    convertToListIdValueBoxes() {
      var boxes = this.$store.state.all_box.boxes;
      var list = [];
      for (const item of boxes) {
        list.push({
          id: item.bo_id,
          value: item.bo_name,
        });
      }

      return list;
    },
    convertToListIdValueSaleTypes() {
      var saleTypes = this.$store.state.sale_type.saleTypes;
      var list = [];
      for (const item of saleTypes) {
        list.push({
          id: item.st_id,
          value: item.st_name,
        });
      }

      return list;
    },
    convertToListIdValuePlatforms() {
      var platforms = this.$store.state.sale_platform.platforms;
      var list = [];
      for (const item of platforms) {
        list.push({
          id: item.pl_id,
          value: item.pl_name,
        });
      }

      return list;
    },
    convertToListIdValue() {
      var list = [];
      for (const item of this.currencys) {
        list.push({
          id: item.cur_id,
          value: this.$t(item.cur_name),
        });
      }

      return list;
    },
    convertToListIdValueCustomers() {
      var customers = this.$store.state.customer.customers;
      var list = [];
      for (const item of customers) {
        list.push({
          id: item.cu_id,
          value: item.cu_name,
        });
      }

      return list;
    },
    sumDiscount() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += Number(item.si_discount) || 0;
      }
      if (this.cur_id == 2) {
        return parseFloat(sum.toFixed(5));
      }
      return Math.round(sum);
    },
    //sum price of all items
    sumPrice() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum +=
          this.getPriceByUnit(item) * (Number(item.si_quantity) || 0) -
          (Number(item.si_discount) || 0);
      }

      // For dollar currency (cur_id == 2), keep up to 5 decimal places
      // For dinar currency (cur_id == 1), round to whole number
      if (this.cur_id == 2) {
        // Dollar: keep up to 5 decimal places
        const roundedSum = parseFloat(sum.toFixed(5));
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.in_paid = roundedSum.toFixed(5);
        return roundedSum;
      } else {
        // Dinar: round to whole number
        const roundedSum = Math.round(sum);
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.in_paid = roundedSum.toFixed(0);
        return roundedSum;
      }
    },
    //price withoud discount
    sumPriceWithoutDiscount() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += this.getPriceByUnit(item) * item.si_quantity;
      }
      // For dollar currency (cur_id == 2), keep up to 5 decimal places
      // For dinar currency (cur_id == 1), round to whole number
      if (this.cur_id == 2) {
        return parseFloat(sum.toFixed(5));
      } else {
        return Math.round(sum);
      }
    },
    sumCost() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += this.getCostPriceByUnit(item) * item.si_quantity;
      }
      return sum.toFixed(5);
    },
    totalBonusItems() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += item.si_bonus_quantity || 0;
      }
      return sum;
    },
    hasItemsWithPackages() {
      return this.getSoldListByCurrentCurrency.some(item => 
        item.i_packages && item.i_packages.length > 0
      );
    },
    getSoldListByCurrentCurrency() {
      var list = [];
      for (const item of this.sold_list) {
        //cehck this.cur_id == item.cur_id nothing to do otherwise convert price if this.cur_id==1 convert to dinar if this.cur_id==2 convert to dollar
        list.push(item);
      }

      return list;
    },
    greenCartColspan() {
      return (
        4 +
        (this.showCartBonusColumn ? 1 : 0) +
        (this.showCartPackagesColumn ? 1 : 0) +
        (this.showCartDiscountColumn ? 1 : 0)
      );
    },
    classicCartColspan() {
      return (
        7 +
        (this.showCartBonusColumn ? 1 : 0) +
        (this.showCartPackagesColumn && this.hasItemsWithPackages ? 1 : 0) +
        (this.showCartDiscountColumn ? 1 : 0)
      );
    },
    checkIfEdit() {
      if (this.$route.params.id) {
        return true;
      }
      return false;
    },
  },

  methods: {
    lineDecimalInputDisplay,
    lineSalePriceInputValue(item) {
      return lineDecimalInputDisplay(item.si_sale_price);
    },
    onLineSalePriceLocaleInput(e, item, { recalcDiscount } = {}) {
      item.si_sale_price = processDecimalInputEvent(e, "");
      if (recalcDiscount) {
        this.calculateDis(item, numericFromDecimalField(item.si_discount, 0));
      }
    },
    onGreenLineQtyLocaleInput(e, item) {
      const next = sanitizeIntegerInput(e.target.value);
      if (e.target.value !== next) e.target.value = next;
      const n = parseInt(next, 10);
      item.si_quantity = next === "" ? 1 : (Number.isFinite(n) ? Math.max(1, n) : 1);
      this.calculateDue(item, item.si_quantity);
    },
    onClassicLineQtyLocaleInput(e, item) {
      const next = sanitizeIntegerInput(e.target.value);
      if (e.target.value !== next) e.target.value = next;
      const n = parseInt(next, 10);
      item.si_quantity = next === "" ? 0 : (Number.isFinite(n) ? Math.max(0, n) : 0);
      this.calculateDue(item, item.si_quantity);
    },
    onLineBonusQtyLocaleInput(e, item) {
      const next = sanitizeIntegerInput(e.target.value);
      if (e.target.value !== next) e.target.value = next;
      const n = parseInt(next, 10);
      item.si_bonus_quantity = next === "" ? 0 : (Number.isFinite(n) ? Math.max(0, n) : 0);
    },
    onLineDiscountLocaleInput(e, item) {
      item.si_discount = processDecimalInputEvent(e, 0);
      this.calculateDis(item, numericFromDecimalField(item.si_discount, 0));
    },
    onRootDecimalLocaleInput(e, key, afterCallback) {
      const stored = processDecimalInputEvent(e, 0);
      this[key] = stored;
      if (typeof afterCallback === "function" && typeof stored === "number") {
        afterCallback.call(this);
      }
    },
    totalDiscountInputDisplay(value) {
      if (value === "" || value === null || value === undefined) return "";
      return String(value);
    },
    onTotalDiscountLocaleInput(e) {
      const next = sanitizeNumericInput(e.target.value);
      if (e.target.value !== next) {
        e.target.value = next;
      }
      this.total_discount = applyRootDecimalLocaleInput(next, 0);
      const display = this.totalDiscountInputDisplay(this.total_discount);
      if (e.target.value !== display) {
        e.target.value = display;
      }
      if (typeof this.total_discount === "number") {
        this.applyTotalDiscount();
      }
    },
    onPercentageDiscountLocaleInput(e) {
      const next = sanitizeNumericInput(e.target.value);
      if (e.target.value !== next) e.target.value = next;
      this.percentage_discount = applyPercentageDecimalInput(next);
      if (typeof this.percentage_discount === "number") {
        this.applyPercentageDiscount();
      }
    },
    getPrintableInvoiceHeader(rawHeader) {
      const header = String(rawHeader || "").trim();
      if (!header) return "";
      if (header === "وەسڵی فرۆشتن") return this.$t("invoice_title");
      return this.$te(header) ? this.$t(header) : header;
    },
    handlePosKeypadInput(value) {
      if (this.selectedKeypadField !== "in_paid") {
        this.selectedKeypadField = "in_paid";
      }
      const current = this.in_paid == null ? "" : String(this.in_paid);
      if (value === "." && current.includes(".")) return;
      const next = current + String(value);
      const parsed = Number(next);
      if (!Number.isNaN(parsed)) {
        this.in_paid = next;
      }
    },
    loadUiLayoutSettings() {
      try {
        const saved = localStorage.getItem("sale_pos_ui_layout");
        if (!saved) {
          this.isUiLayoutLoaded = true;
          return;
        }

        const parsed = JSON.parse(saved);
        this.showProductPanel =
          typeof parsed.showProductPanel === "boolean"
            ? parsed.showProductPanel
            : this.showProductPanel;
        this.isShowCategories =
          typeof parsed.isShowCategories === "boolean"
            ? parsed.isShowCategories
            : this.isShowCategories;
        this.isShowItems =
          typeof parsed.isShowItems === "boolean"
            ? parsed.isShowItems
            : this.isShowItems;
        this.isShowDetail =
          typeof parsed.isShowDetail === "boolean"
            ? parsed.isShowDetail
            : this.isShowDetail;
        this.compactItemCards =
          typeof parsed.compactItemCards === "boolean"
            ? parsed.compactItemCards
            : this.compactItemCards;
        this.showCartBonusColumn =
          typeof parsed.showCartBonusColumn === "boolean"
            ? parsed.showCartBonusColumn
            : this.showCartBonusColumn;
        this.showCartPackagesColumn =
          typeof parsed.showCartPackagesColumn === "boolean"
            ? parsed.showCartPackagesColumn
            : this.showCartPackagesColumn;
        this.showCartDiscountColumn =
          typeof parsed.showCartDiscountColumn === "boolean"
            ? parsed.showCartDiscountColumn
            : this.showCartDiscountColumn;
        this.uiDesignMode =
          parsed.uiDesignMode === "green" ? "green" : "classic";
        if (this.uiDesignMode === "classic") {
          this.showProductPanel = true;
          this.isShowCategories = true;
          this.isShowItems = true;
          this.isShowDetail = true;
          this.compactItemCards = false;
        }
      } catch (error) {
        console.warn("Failed to load sale POS layout settings", error);
      } finally {
        this.isUiLayoutLoaded = true;
      }
    },
    saveUiLayoutSettings() {
      if (!this.isUiLayoutLoaded) return;
      try {
        localStorage.setItem(
          "sale_pos_ui_layout",
          JSON.stringify({
            showProductPanel: this.showProductPanel,
            isShowCategories: this.isShowCategories,
            isShowItems: this.isShowItems,
            isShowDetail: this.isShowDetail,
            compactItemCards: this.compactItemCards,
            showCartBonusColumn: this.showCartBonusColumn,
            showCartPackagesColumn: this.showCartPackagesColumn,
            showCartDiscountColumn: this.showCartDiscountColumn,
            uiDesignMode: this.uiDesignMode,
          })
        );
      } catch (error) {
        console.warn("Failed to save sale POS layout settings", error);
      }
    },
    loadPosPreferences() {
      try {
        const saved = localStorage.getItem("sale_pos_preferences");
        if (!saved) {
          this.isPosPreferencesLoaded = true;
          return;
        }
        const parsed = JSON.parse(saved);
        const savedCurId = Number(parsed.cur_id);
        const savedPrintType = String(parsed.isPrint ?? "");
        if (savedCurId === 1 || savedCurId === 2) {
          this.cur_id = savedCurId;
        }
        if (["1", "2", "3"].includes(savedPrintType)) {
          this.isPrint = savedPrintType;
        }
      } catch (error) {
        console.warn("Failed to load sale POS preferences", error);
      } finally {
        this.isPosPreferencesLoaded = true;
      }
    },
    savePosPreferences() {
      if (!this.isPosPreferencesLoaded) return;
      try {
        localStorage.setItem(
          "sale_pos_preferences",
          JSON.stringify({
            cur_id: Number(this.cur_id),
            isPrint: String(this.isPrint),
          })
        );
      } catch (error) {
        console.warn("Failed to save sale POS preferences", error);
      }
    },
    handleGlobalQtyArrowKeys(event) {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
        return;
      }
      if (!this.sold_list || this.sold_list.length === 0) {
        return;
      }

      const lastItem =
        this.sold_list.find((item) => item.i_id === this.lastAddedItemId) ||
        this.sold_list[this.sold_list.length - 1];

      if (!lastItem) return;

      event.preventDefault();
      event.stopPropagation();

      const currentQty = Number(lastItem.si_quantity) || 1;
      const delta = event.key === "ArrowUp" ? 1 : -1;
      const minQty = 1;
      const maxQty = Number(lastItem.i_quantity) || currentQty;
      const nextQty = Math.max(minQty, Math.min(maxQty, currentQty + delta));

      if (nextQty !== currentQty) {
        this.updateInvoice(lastItem, nextQty);
      }
    },
    handleSearchInput(value) {
      this.$store.commit('sale_pos/SET_SEARCH_POS', value);
    },
    async selectGreenQuickSearchItem(item) {
      await this.addItemToInvoice(item, { skipQuantityFocus: true });
      this.greenQuickSearchQuery = "";
      this.focusBarcodeInput();
    },
    async buildInvoiceIdList() {
      try {
        const tempObj = { page: 1, rowsPerPage: 1000 };
        tempObj.search = "";
        tempObj.searchBarcode = "";
        const res = await this.$store.dispatch("sold_list/getItems", tempObj);
        if (res && res.data && Array.isArray(res.data.data)) {
          // ensure numeric sort ascending then we can pick last for latest
          this.invoiceIdList = res.data.data
            .map((x) => x.in_id)
            .sort((a, b) => a - b);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async goToPrevInvoice() {
      // show full page loading immediately
      this.$store.state.loading = true;
      // Always navigate to the last (latest) invoice as requested
      if (!this.invoiceIdList.length) {
        await this.buildInvoiceIdList();
      }
      if (!this.invoiceIdList.length) return;
      const lastId = this.invoiceIdList[this.invoiceIdList.length - 1];
      if (lastId)
        this.$router.push({ name: "EditSale", params: { id: lastId } });
    },
    openCalculator() {
      console.log(
        "SalePos: openCalculator called, isLoadingData:",
        this.isLoadingData
      );
      // Prevent opening if page is still loading
      if (this.isLoadingData) {
        console.log("SalePos: Calculator blocked - page still loading");
        return;
      }

      this.isCalculatorOpen = true;

      // Ensure proper initialization after next tick
      this.$nextTick(() => {
        // Force re-render if needed
        this.$forceUpdate();
      });
    },
    closeCalculator() {
      this.isCalculatorOpen = false;
      this.$nextTick(() => {
        // Force re-render if needed
        this.$forceUpdate();
      });
    },
    async getCustomerDebt() {
      this.isShowDebts = false;
      try {
        var res = await this.$store.dispatch("customer/getCurrentDebt", {
          cu_id: this.cu_id,
          //invoice date
          created_at: new Date().toISOString(),
        });

        if (res && res.balance_before_date) {
          this.beforeDebtIQD = res.balance_before_date.IQD || 0;
          this.beforeDebtUSD = res.balance_before_date.USD || 0;
        } else {
          this.beforeDebtIQD = 0;
          this.beforeDebtUSD = 0;
        }
        this.isShowDebts = true;
      } catch (error) {
        console.error("Error fetching customer debt:", error);
        this.beforeDebtIQD = 0;
        this.beforeDebtUSD = 0;
        this.isShowDebts = true;
      }
    },
    async getItemPurchaseHistory(itemId) {
      // Only fetch if customer is selected
      if (!this.cu_id) {
        return;
      }

      // Don't fetch if we already have the data
      if (this.$store.state.sale_pos.customerItemHistory[itemId]) {
        return;
      }

      try {
        await this.$store.dispatch("sale_pos/getCustomerItemHistory", {
          cu_id: this.cu_id,
          i_id: itemId,
        });
      } catch (error) {
        console.error("Error fetching purchase history:", error);
      }
    },
    clearCustomerItemHistory() {
      // Clear customer item history when customer changes
      this.$store.state.sale_pos.customerItemHistory = {};
    },
    showPriceHistory(item) {
      this.selectedItemForHistory = item;
      this.showPriceHistoryDialog = true;
      // Fetch history if not already loaded
      this.getItemPurchaseHistory(item.i_id);
    },
    closePriceHistoryDialog() {
      this.showPriceHistoryDialog = false;
      this.selectedItemForHistory = null;
    },
    getCustomerName(cu_id1) {
      const selectedId =
        typeof cu_id1 === "object" && cu_id1 !== null
          ? cu_id1.id ?? cu_id1.cu_id
          : cu_id1;
      var customers = this.$store.state.customer.customers;

      for (const item of customers) {
        if (item.cu_id == selectedId) {
          return item.cu_name;
        }
      }
      return "";
    },
    getCustomerDisplayLabel(cu_id, customerName, customerPhone) {
      const cleanName = String(customerName ?? "").trim();
      const cleanPhone = String(customerPhone ?? "").trim();
      if (cleanName) {
        return cleanPhone ? `${cleanName} - ${cleanPhone}` : cleanName;
      }
      const fallbackName = this.getCustomerName(cu_id);
      return fallbackName ? `${fallbackName} - ${cu_id}` : String(cu_id ?? "-");
    },
    showWhatsAppDialog() {
      this.showWhatsAppModal = true;
    },
    handleWhatsAppMessageSubmit() {
      this.showWhatsAppModal = false;
      this.getOrderFromWhatsApp1();
    },
    getOrderFromWhatsApp1() {
      // Reset sold list
      this.sold_list = [];

      // Check if message text exists
      if (!this.message?.text || this.message.text.trim() === "") {
        this.$refs.ResultDialog.show({
          message: this.$t("no_message_found"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      const regExp = /(.+?)\s*->\s*[^*]+?\*\D*(\d+)\D*\*/gu;
      const matches = [...this.message.text.matchAll(regExp)];

      const orders = matches.map((match) => {
        const barcode = match[1].trim();
        const quantity = parseInt(match[2]);
        return { barcode, quantity };
      });

      if (orders.length === 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("no_valid_orders_found"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      let notAvailable = [];

      orders.forEach((order) => {
        const item = this.filterItemsPos.find(
          (itm) => itm.i_barcode === order.barcode
        );

        if (item) {
          if (item.i_quantity >= order.quantity) {
            for (let i = 0; i < order.quantity; i++) {
              this.addItemToInvoice(item); // your method
            }
          } else {
            notAvailable.push(order.barcode);
          }
        } else {
          notAvailable.push(order.barcode);
        }
      });

      if (notAvailable.length > 0) {
        this.$refs.ResultDialog.show({
          message:
            this.$t(
              "some_items_are_not_available_in_stock_please_check_the_following_barcodes"
            ) +
            ":\n" +
            notAvailable.join("\n"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
      }
    },
    getCurrentLocaleValue() {
      const locale = this.$i18n?.locale;
      if (typeof locale === "string") return locale;
      if (locale && typeof locale.value === "string") return locale.value;
      return localStorage.getItem("lang_wise_manager") || "ku";
    },
    setCurrentLocaleValue(locale) {
      if (!locale) return;
      if (typeof this.$i18n?.locale === "string") {
        this.$i18n.locale = locale;
      } else if (this.$i18n?.locale && "value" in this.$i18n.locale) {
        this.$i18n.locale.value = locale;
      }
    },
    async modalPrintID() {
      const previousLocale = this.getCurrentLocaleValue();
      const targetLocale = this.printInvoiceLanguage || previousLocale;
      if (targetLocale !== previousLocale) {
        this.setCurrentLocaleValue(targetLocale);
        await this.$nextTick();
      }

      // Show print preview first so #invoice_print reflects the current customer before capture.
      this.isModalPrint = true;
      await this.$nextTick();

      const printContents = document.getElementById("invoice_print").innerHTML;
      // Close preview immediately; print uses captured HTML (avoids UI/modal stuck open while cart clears).
      this.isModalPrint = false;
      await this.$nextTick();

      // Create a hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";

      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
    <html>
      <head>
        <title>Print</title>
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.16/dist/tailwind.min.css" rel="stylesheet">
        <style>
          body {
            direction: rtl;
            font-family: 'Rabar_022', sans-serif;
          }
        </style>
        <link rel="stylesheet" href="https://kcstore.online/fonts/Rabar_022.6835fa5e.ttf">
      </head>
      <body>
        <div>${printContents}</div>
      </body>
    </html>
  `);
      doc.close();

      // Wait a moment before printing
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.onafterprint = () => {
            if (targetLocale !== previousLocale) {
              this.setCurrentLocaleValue(previousLocale);
            }
          };
          iframe.contentWindow.print();
          if (targetLocale !== previousLocale) {
            this.setCurrentLocaleValue(previousLocale);
          }
        }, 50); // slight delay ensures content is loaded
      };
    },
    classesPrintModal() {
      return {
        "modal  bg-opacity-5 h-full   w-full  fixed top-0 left-0  justify-center   hidden overflow-y-auto":
          !this.isModalPrint,
        "modal  bg-opacity-5 h-full   w-full  fixed top-0 left-0  justify-center  flex overflow-y-auto ":
          this.isModalPrint,
      };
    },
    getPosData() {
      this.sold_list = [];

      this.$store.dispatch("sale_pos/getItemsPOS", {
        in_type: this.in_type,
      });
    },
    printStatus() {
      this.savePosPreferences();
    },
    async changeCurrencyType() {
      if (this.sold_list.length == 0) {
        if (this.cur_id == 1) {
          this.cur_id = 2;
        } else {
          this.cur_id = 1;
        }
        return;
      }
      const ok = await this.$refs.confirmDialogue.show({
        message: this.$t("changing_currency_will_clear_invoice_are_you_sure"),
        okButton: this.$t("yes"),
      });

      if (!ok) {
        return;
      }
      this.sold_list = [];
      this.in_paid = 0;
      this.in_price = 0;
      this.in_discount = 0;
      this.in_due = 0;
      this.total_discount = 0;
      this.percentage_discount = 0;

      if (this.cur_id == 1) {
        this.cur_id = 2;
      } else {
        this.cur_id = 1;
      }
    },
    getUnit(item) {
      return item.un_name;
    },
    // Format price based on currency — up to 5 decimal places (dollar and dinar via formatNumber)
    formatPriceByCurrency(value) {
      if (this.cur_id == 2) {
        const num = parseFloat(value);
        if (!Number.isFinite(num)) return "0";
        let fixed = num.toFixed(5);
        if (fixed.includes(".")) {
          fixed = fixed.replace(/(\.\d*?)0+$/, "$1");
          fixed = fixed.replace(/\.$/, "");
        }
        const parts = fixed.split(".");
        const intWithSep = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.length > 1 ? `${intWithSep}.${parts[1]}` : intWithSep;
      }
      return this.formatNumber(value);
    },
    getInvoicePrintTotals() {
      let itemsPrice = 0;
      let discountTotal = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        const qty = Number(item.si_quantity) || 0;
        const price = Number(this.getPriceByUnit(item)) || 0;
        const discount = Number(item.si_discount) || 0;
        itemsPrice += price * qty;
        discountTotal += discount;
      }
      if (this.cur_id == 2) {
        itemsPrice = parseFloat(itemsPrice.toFixed(5));
        discountTotal = parseFloat(discountTotal.toFixed(5));
      } else {
        itemsPrice = Math.round(itemsPrice);
        discountTotal = Math.round(discountTotal);
      }
      let finalPrice =
        this.cur_id == 2
          ? parseFloat((itemsPrice - discountTotal).toFixed(5))
          : Math.round(itemsPrice - discountTotal);
      if (!Number.isFinite(finalPrice)) {
        finalPrice = Number(this.in_price) || 0;
      }
      return { itemsPrice, discountTotal, finalPrice };
    },
    getPriceByUnit(item) {
      const raw = item?.si_sale_price;
      if (raw === "" || raw === "." || raw === null || raw === undefined) {
        return this.getSelectedSalePrice(item);
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        return this.getSelectedSalePrice(item);
      }
      return parseFloat(n.toFixed(5));
    },
    getPackageSize(item) {
      const size = Number(item?.i_package);
      return Number.isFinite(size) && size > 0 ? size : 1;
    },
    getAvailableQuantityByType(item) {
      const stockPieces = Number(item?.i_quantity || 0);
      if (Number(item?.si_is_package) === 1) {
        const packageSize = this.getPackageSize(item);
        return Math.floor(stockPieces / packageSize);
      }
      return stockPieces;
    },
    getPackagePriceInInvoiceCurrency(item) {
      const packageSize = this.getPackageSize(item);
      const rawPackagePrice = Number(item?.i_package_price);
      const fallbackRawPrice = Number(item?.i_sale_price) * packageSize;
      const packagePrice =
        Number.isFinite(rawPackagePrice) && rawPackagePrice > 0
          ? rawPackagePrice
          : fallbackRawPrice;
      return this.convertPriceToInvoiceCurrency(packagePrice, item?.cur_id);
    },
    getPiecePriceInInvoiceCurrency(item) {
      return this.convertPriceToInvoiceCurrency(item?.i_sale_price, item?.cur_id);
    },
    getSelectedSalePrice(item) {
      const selectedPrice =
        Number(item?.si_is_package) === 1
          ? this.getPackagePriceInInvoiceCurrency(item)
          : this.getPiecePriceInInvoiceCurrency(item);
      const numericPrice = Number(selectedPrice);
      return Number.isFinite(numericPrice)
        ? parseFloat(numericPrice.toFixed(5))
        : 0;
    },
    onItemTypeChange(item) {
      const availableByType = this.getAvailableQuantityByType(item);
      if (item.si_quantity > availableByType) {
        item.si_quantity = availableByType;
      }
      if (item.si_quantity <= 0 && availableByType > 0) {
        item.si_quantity = 1;
      }
      item.si_sale_price = this.getSelectedSalePrice(item);
      this.sold_list = [...this.sold_list];
      this.recalculateDiscounts();
    },
    
    // Helper method to convert price based on currency
    convertPriceToInvoiceCurrency(itemPrice, itemCurrencyId) {
      const numericPrice = Number(itemPrice);
      if (!Number.isFinite(numericPrice)) {
        return 0;
      }
      if (itemCurrencyId == this.cur_id) {
        return numericPrice;
      } else if (itemCurrencyId == 1) {
        // Item is in dinar, invoice is in dollar - convert to dollar
        return numericPrice / this.getDolarPrice;
      } else {
        // Item is in dollar, invoice is in dinar - convert to dinar
        return numericPrice * this.getDolarPrice;
      }
    },
    calculatePackagePrice(item, quantity, convertedBasePrice = null) {
      // Convert base price if not provided
      let basePrice = convertedBasePrice;
      if (basePrice === null) {
        basePrice = this.convertPriceToInvoiceCurrency(item.i_sale_price, item.cur_id);
      }
      
      // If no packages defined, return the converted base price
      if (!item.i_packages || item.i_packages.length === 0) {
        return parseFloat(basePrice.toFixed(5));
      }
      
      // Sort packages by quantity (descending) to find the best match
      const packages = [...item.i_packages].sort((a, b) => b.quantity - a.quantity);
      
      // Find the first package that matches the quantity
      for (const pkg of packages) {
        if (quantity >= pkg.quantity) {
          // Convert package price to invoice currency
          const convertedPackagePrice = this.convertPriceToInvoiceCurrency(pkg.price, item.cur_id);
          const numericPackagePrice = Number(convertedPackagePrice);
          return Number.isFinite(numericPackagePrice)
            ? parseFloat(numericPackagePrice.toFixed(5))
            : 0;
        }
      }
      
      // If no package matches, return the converted base price
      const numericBasePrice = Number(basePrice);
      return Number.isFinite(numericBasePrice)
        ? parseFloat(numericBasePrice.toFixed(5))
        : 0;
    },
    getCostPriceByUnit(item) {
      return item.si_cost_price;
    },
    async getCustomers() {
      await this.$store.dispatch("customer/getCustomers");

      this.cu_id = this.$store.state.customer.customers[0].cu_id;
      this.getCustomerDebt();
    },
    async getPlatforms() {
      await this.$store.dispatch("sale_platform/getPlatforms");

      this.pl_id = this.$store.state.sale_platform.platforms[0].pl_id;
    },
    async getBoxs() {
      await this.$store.dispatch("all_box/getBoxes");

      this.bo_id = this.$store.state.all_box.boxes[0].bo_id;
    },
    async getSaleTypes() {
      await this.$store.dispatch("sale_type/getSaleTypes");

      this.st_id = this.$store.state.sale_type.saleTypes[0].st_id;
    },
    formatNumber(number) {
      return formatNumber(number);
    },
    formatDate(date) {
      return formatDate(date);
    },
    getSaleTypeLabel(item) {
      return Number(item?.si_is_package) === 1 ? this.$t("carton") : this.$t("piece");
    },
    getInvoiceItemName(item) {
      const sourceItem = item?.item || {};
      const nestedItem = sourceItem?.item || {};
      const baseName =
        item?.i_name ||
        sourceItem?.i_name ||
        nestedItem?.i_name ||
        "";
      const baseNameAr =
        item?.i_name_ar ||
        sourceItem?.i_name_ar ||
        nestedItem?.i_name_ar ||
        "";
      const locale = String(this.getCurrentLocaleValue()).toLowerCase();
      if (locale.startsWith("ar") && baseNameAr) return baseNameAr;
      return baseName;
    },
    /** Cashier + A4: quantity + unit (کارتۆن / شەدە / دانە) in qty column */
    getInvoiceQuantityWithUnit(item) {
      const qty = this.formatNumber(item.si_quantity);
      if (Number(item?.si_is_package) === 1) {
        return `${qty} ${this.$t("carton")}`;
      }
      return `${qty} ${this.$t("piece")}`;
    },
    getInvoicePiecesPerBox(item) {
      return this.getPackageSize(item);
    },
    getInvoiceBoxCount(item) {
      const qty = Number(item?.si_quantity || 0);
      if (Number(item?.si_is_package) === 1) return qty;
      const piecesPerBox = this.getInvoicePiecesPerBox(item);
      if (!piecesPerBox) return 0;
      return qty / piecesPerBox;
    },
    getInvoiceBoxCountDisplay(item) {
      const isPackageSale = Number(item?.si_is_package) === 1;
      const boxCount = this.getInvoiceBoxCount(item);
      if (!isPackageSale || boxCount < 1) return "-";
      return this.formatNumber(boxCount);
    },
    getInvoicePiecesCount(item) {
      const qty = Number(item?.si_quantity || 0);
      if (Number(item?.si_is_package) === 1) {
        return qty * this.getInvoicePiecesPerBox(item);
      }
      return qty;
    },
    /**
     * Carton price on printed invoice — from line si_sale_price (getPriceByUnit),
     * not catalog i_package_price.
     */
    getInvoiceBoxPrice(item) {
      const unit = this.getPriceByUnit(item);
      const perBox = this.getInvoicePiecesPerBox(item);
      if (Number(item?.si_is_package) === 1) {
        return unit;
      }
      return unit * perBox;
    },
    hasInvoiceAtLeastOneBox(item) {
      return this.getInvoiceBoxCount(item) >= 1;
    },
    getInvoicePiecesPerBoxDisplay(item) {
      if (!this.hasInvoiceAtLeastOneBox(item)) return "-";
      return this.formatNumber(this.getInvoicePiecesPerBox(item));
    },
    getInvoiceBoxPriceDisplay(item) {
      if (!this.hasInvoiceAtLeastOneBox(item)) return "-";
      return this.formatPriceByCurrency(this.getInvoiceBoxPrice(item));
    },
    getInvoicePiecePriceFromBox(item) {
      if (Number(item?.si_is_package) !== 1) {
        return this.getPriceByUnit(item);
      }
      const piecesPerBox = this.getInvoicePiecesPerBox(item);
      const boxPrice = this.getInvoiceBoxPrice(item);
      if (!piecesPerBox) return 0;
      return boxPrice / piecesPerBox;
    },
    calculateDue(item, num) {
      this.updateInvoice(item, num);
    },
    calculateDis(item, discount) {
      this.updateInvoiceDiscount(item, discount);
    },
    setQuantityFromPackage(item, packageQuantity, packagePrice) {
      // Set the quantity to the package quantity and update the price
      // Find the cart item and update it directly
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_quantity = packageQuantity;
          soldItem.si_is_package = 1;
          // Convert package price to invoice currency
          const convertedPackagePrice = this.convertPriceToInvoiceCurrency(packagePrice, item.cur_id);
          const numericPackagePrice = Number(convertedPackagePrice);
          soldItem.si_sale_price = Number.isFinite(numericPackagePrice)
            ? parseFloat(numericPackagePrice.toFixed(5))
            : 0;
          break;
        }
      }
      
      // Trigger reactivity
      this.sold_list = [...this.sold_list];
      
      // Recalculate discounts
      this.recalculateDiscounts();
    },
    async addSaleItem() {
      if (this.$store.state.sale_pos.isBtnLoading) return;

      this.isSubmited = true;
      if (
        this.checkRulesIsError(this.bo_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.cu_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.pl_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.st_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.cur_id, "add", this.rules).isError ||
        this.checkRulesIsError(this.in_paid, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumPrice, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumDiscount, "add", this.rules).isError ||
        this.checkRulesIsError(this.getInDue, "add", this.rules).isError ||
        this.checkRulesIsError(this.getDolarPrice, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumCost, "add", this.rules).isError
      ) {
        this.$refs.ResultDialog.show({
          message: this.$t("please_fill_all_fields"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      //check if in paid > in price
      if (this.in_paid > this.sumPrice) {
        this.$refs.ResultDialog.show({
          message: this.$t("paid_money_more_than_total_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      //check if in_paid or in_price or in_discount or in_due is not number or < 0
      if (
        isNaN(this.in_paid) ||
        isNaN(this.in_price) ||
        isNaN(this.in_discount) ||
        isNaN(this.in_due) ||
        this.in_paid < 0 ||
        this.in_price < 0 ||
        this.in_discount < 0 ||
        this.in_due < 0
      ) {
        this.$refs.ResultDialog.show({
          message: this.$t("please_enter_valid_number_for_all_fields"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      if (this.sold_list.length == 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("no_item_in_invoice"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      this.$store.state.sale_pos.isBtnLoading = true;
      var tempSoldList = [];
      for (const item of this.getSoldListByCurrentCurrency) {
        //find item in getSoldListByCurrentCurrency and get price and cost price

        tempSoldList.push({
          i_id: item.i_id,
          i_barcode: item.i_barcode,
          si_quantity: item.si_quantity,
          si_bonus_quantity: item.si_bonus_quantity || 0,
          si_is_package: Number(item.si_is_package) === 1 ? 1 : 0,
          cur_id: item.cur_id,
          si_sale_price: this.getPriceByUnit(item),
          si_discount: item.si_discount,
          si_cost_price: item.si_cost_price,
          pui_id: item.pui_id,
        });
      }

      // eslint-disable-next-line no-unreachable
      var res = await this.$store.dispatch("sale_pos/addNewItem", {
        cu_id: this.cu_id,
        bo_id: this.bo_id,
        cur_id: this.cur_id,
        st_id: this.st_id,
        pl_id: this.pl_id,
        in_paid: this.in_paid,
        in_price: this.sumPrice,
        in_cost: this.sumCost,
        in_discount: this.sumDiscount,
        in_due: this.getInDue,
        in_main_currency_price: this.getDolarPrice,
        in_note: this.in_note,
        customer_name: this.customer_name,
        customer_phone_number: this.customer_phone_number,
        sold_items: tempSoldList,
        created_at: this.created_at,
        in_type: this.in_type,
      });
      if (res.status == 200) {
        // Snapshot customer shown on invoice BEFORE any async print or server field overwrites.
        const printCustomerSnapshot = {
          cu_id: this.cu_id,
          customer_name: this.customer_name,
          customer_phone_number: this.customer_phone_number,
        };
        this.created_at = res.data.created_at;
        this.in_id = res.data.in_id;
        this.cu_id = res.data.cu_id;

        if (this.isPrint == "1") {
          await new Promise((resolve) => setTimeout(resolve, 500));
          await this.modalPrintID();
        } else if (this.isPrint == "3") {
          await new Promise((resolve) => setTimeout(resolve, 500));
          this.in_id = res.data.in_id;
          // Must await: addSaleItem used to reset cu_id while this method was still awaiting debt/print prep.
          await this.modalPrintIDA4(
            res.data.created_at,
            printCustomerSnapshot
          );
        }
        await this.$store.dispatch("navbar/getItems");

        if (this.isPrint == "2" || this.isPrint == "1") {
          this.getPosData();
          this.clearInvoice(false);
        }
        this.isSubmited = false;

        // After cashier/A4 print pipeline, restore defaults for next sale (not before print HTML is built).
        this.cu_id = this.$store.state.customer.customers[0].cu_id;
        this.pl_id = this.$store.state.sale_platform.platforms[0].pl_id;
        this.st_id = this.$store.state.sale_type.saleTypes[0].st_id;
      }
      if (this.isPrint == "2" || this.isPrint == "1") {
        this.$store.state.sale_pos.isBtnLoading = false;
      }
    },
    checkRulesIsError(value, type, rules) {
      var isError = false;
      var errorMsg = "";
      if (type == "add") {
        if (this.isSubmited == false)
          return {
            isError: false,
            errorMsg: "",
          };
      }
      if (type == "edit") {
        if (this.isSubmitedEdit == false)
          return {
            isError: false,
            errorMsg: "",
          };
      }

      for (const rule of rules) {
        if (rule(value) !== true) {
          isError = true;
          errorMsg = rule(value);
          // Stop the loop
          break;
        } else {
          isError = false;
          errorMsg = "";
        }
      }

      return {
        isError: isError,
        errorMsg: errorMsg,
      };
    },
    addRecievedMoney(curency, amount) {
      //check this.cur_id == curency.cur_id
      if (this.cur_id == curency) {
        this.recivedMoney += amount;
      } else {
        if (this.cur_id == 1) {
          //convert to dinar
          this.recivedMoney += amount * this.getDolarPrice;
        } else {
          //convert to dollar
          this.recivedMoney += amount / this.getDolarPrice;
        }
      }
    },

    async clearInvoice(isCheck) {
      if (isCheck) {
        const ok = await this.$refs.confirmDialogue.show({
          message: this.$t("are_you_sure_you_want_to_clear_the_cart"),
          okButton: this.$t("yes"),
        });

        if (!ok) {
          return;
        }
      }
      if (this.customer_name != null && this.customer_name != "") {
        //return all customer data to default
        this.getCustomers();
      }
      this.sold_list = [];
      this.in_paid = 0;
      this.in_price = 0;
      this.in_discount = 0;
      this.in_due = 0; 
      this.total_discount = 0;
      this.percentage_discount = 0;
      this.in_main_currency_price = 0;
      this.in_note = "";
      this.customer_name = "";
      this.customer_phone_number = "";
      this.created_at = "";
      this.recivedMoney = 0;
    },
    async showModal(item) {
      this.current = item;
      await this.$refs.actionModal.show();
    },
    async addItemToInvoice(item, options = {}) {
      const skipQuantityFocus = options.skipQuantityFocus === true;
      const refocusBarcodeAfterMs = options.refocusBarcodeAfterMs || 0;
      //check if item is already in the list
      //check if expired date is not null and expired date < current date
      if (item.i_expire_date != null) {
        var today = new Date();
        var date = new Date(item.i_expire_date);
        if (date < today) {
          //show confirm dialog
          const ok = await this.$refs.confirmDialogue.show({
            message: this.$t(
              "this_item_is_expired_are_you_sure_want_to_add_it"
            ),
            okButton: this.$t("yes"),
          });

          if (!ok) {
            return;
          }
        }
      }
      if (item.i_quantity <= 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("this_item_is_not_available"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      var isExist = false;
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          isExist = true;
          if (soldItem.si_quantity >= this.getAvailableQuantityByType(soldItem)) {
            this.$refs.ResultDialog.show({
              message: this.$t("not_enough_item_in_stock"),
              okButton: this.$t("done"),
              isSuccess: false,
            });
            return;
          }
          soldItem.si_quantity++;
          break;
        }
      }

      const price = Number(item.i_sale_price);
      const dolarPrice = Number(this.getDolarPrice);

      if (isNaN(price) || isNaN(dolarPrice)) {
        console.warn("Invalid price or dollar value", { price, dolarPrice });
      } else {
        // The following calculation is not used, so it has been removed to avoid unused variable warnings.
      }

      if (isExist == false) {
        const canUseCarton =
          Number(item.i_package) > 0 && Number(item.i_package_price) > 0;

        var obj = {
          i_id: item.i_id,
          i_barcode: item.i_barcode,
          si_quantity: 1,
          si_bonus_quantity: 0,
          cur_id: item.cur_id,
          si_sale_price: 0,
          si_discount: 0,
          i_name: item.i_name,
          i_name_ar: item.i_name_ar,
          un_name: item.unit.un_name,
          //   purchase_items: item.purchase_items,
          i_quantity: item.i_quantity,
          si_cost_price: item.i_cost_price,
          i_packages: item.i_packages,
          i_sale_price: item.i_sale_price, // Store original price for package calculations
          i_package: item.i_package,
          i_package_price: item.i_package_price,
          si_is_package: canUseCarton ? 1 : 0,
        };
        if (
          obj.si_is_package === 1 &&
          this.getAvailableQuantityByType(obj) < 1
        ) {
          obj.si_is_package = 0;
        }
        obj.si_sale_price = this.getSelectedSalePrice(obj);
        //add pui_id if purchase_items is not null

        this.sold_list.push(obj);
      } else {
        this.sold_list = [...this.sold_list];
      }

      // Fetch purchase history for this item
      this.getItemPurchaseHistory(item.i_id);

      // Recalculate discounts after adding item
      this.recalculateDiscounts();

      // Set last added item ID and focus on quantity input
      this.lastAddedItemId = item.i_id;
      this.$nextTick(() => {
        // In classic mode, keep focus stable and do not jump to quantity field.
        if (this.uiDesignMode === "classic") {
          return;
        }

        if (skipQuantityFocus) {
          if (refocusBarcodeAfterMs > 0) {
            if (this.barcodeRefocusTimer) {
              clearTimeout(this.barcodeRefocusTimer);
            }
            this.barcodeRefocusTimer = setTimeout(() => {
              this.focusBarcodeInput();
            }, refocusBarcodeAfterMs);
          } else {
            this.focusBarcodeInput();
          }
          return;
        }

        // Try to focus on the quantity input field
        const refName = `quantity-input-${item.i_id}`;
        const quantityInput = this.$refs[refName];
        
        if (quantityInput) {
          // Handle both array (if multiple refs exist) and single element
          const inputElement = Array.isArray(quantityInput) 
            ? quantityInput[quantityInput.length - 1] 
            : quantityInput;
          
          if (inputElement && inputElement.focus) {
            inputElement.focus();
            // Select all text so user can easily type a new number
            if (inputElement.select) {
              inputElement.select();
            }
          }
        }

        if (refocusBarcodeAfterMs > 0) {
          if (this.barcodeRefocusTimer) {
            clearTimeout(this.barcodeRefocusTimer);
          }
          this.barcodeRefocusTimer = setTimeout(() => {
            this.focusBarcodeInput();
          }, refocusBarcodeAfterMs);
        }
      });
    },
    updateInvoice(item, num) {
      if (num <= 0) {
        this.removeItem(item.i_id);
        return;
      }
      if (num > this.getAvailableQuantityByType(item)) {
        this.$refs.ResultDialog.show({
          message: this.$t("not_enough_item_in_stock"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_quantity = num;
          break;
        }
      }

      this.sold_list = [...this.sold_list];

      // Recalculate discounts after quantity change
      this.recalculateDiscounts();
    },
    updateInvoiceDiscount(item, discount) {
      if (discount > item.i_sale_price) {
        this.$refs.ResultDialog.show({
          message: this.$t("discount_more_than_item_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_discount = discount;
          break;
        }
      }

      this.sold_list = [...this.sold_list];
    },
    applyTotalDiscount() {
      if (this.sold_list.length === 0) {
        return;
      }

      const totalDiscount = numericFromDecimalField(this.total_discount, 0);

      // Clear percentage discount when using total discount
      this.percentage_discount = 0;

      // If total discount is 0 or negative, clear all item discounts
      if (totalDiscount <= 0) {
        for (const item of this.sold_list) {
          item.si_discount = 0;
        }
        this.sold_list = [...this.sold_list];
        return;
      }

      // Calculate total price without any discounts
      let totalPriceWithoutDiscount = 0;
      for (const item of this.sold_list) {
        totalPriceWithoutDiscount +=
          this.getPriceByUnit(item) * item.si_quantity;
      }

      if (totalDiscount > totalPriceWithoutDiscount) {
        this.$refs.ResultDialog.show({
          message: this.$t("discount_more_than_total_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        this.total_discount = 0;
        return;
      }

      // Distribute the total discount proportionally among items
      let totalCalculatedDiscount = 0;
      for (const item of this.sold_list) {
        const itemPrice = this.getPriceByUnit(item) * item.si_quantity;
        const proportionalDiscount =
          (itemPrice / totalPriceWithoutDiscount) * totalDiscount;
        item.si_discount = Math.round(proportionalDiscount * 100000) / 100000; // Round to 5 decimal places
        totalCalculatedDiscount += item.si_discount;
      }

      // Adjust the last item's discount to ensure total discount equals the desired amount
      if (this.sold_list.length > 0) {
        const difference = totalDiscount - totalCalculatedDiscount;
        const lastItem = this.sold_list[this.sold_list.length - 1];
        lastItem.si_discount =
          Math.round((lastItem.si_discount + difference) * 100000) / 100000;
      }

      this.sold_list = [...this.sold_list];
    },
    applyPercentageDiscount() {
      if (this.sold_list.length === 0) {
        return;
      }

      // Clear total discount when using percentage discount
      this.total_discount = 0;

      // If percentage discount is 0 or negative, clear all item discounts
      if (this.percentage_discount <= 0) {
        for (const item of this.sold_list) {
          item.si_discount = 0;
        }
        this.sold_list = [...this.sold_list];
        return;
      }

      // Validate percentage range
      if (this.percentage_discount > 100) {
        this.$refs.ResultDialog.show({
          message: this.$t("percentage_discount_cannot_exceed_100"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        this.percentage_discount = 0;
        return;
      }

      // Apply percentage discount to each item
      let totalCalculatedDiscount = 0;
      for (const item of this.sold_list) {
        const itemPrice = this.getPriceByUnit(item) * item.si_quantity;
        const discountAmount = (itemPrice * this.percentage_discount) / 100;
        item.si_discount = Math.round(discountAmount * 100000) / 100000; // Round to 5 decimal places
        totalCalculatedDiscount += item.si_discount;
      }

      // Round the total discount to a whole number and adjust the last item
      const roundedTotalDiscount = Math.round(totalCalculatedDiscount);
      if (
        this.sold_list.length > 0 &&
        roundedTotalDiscount !== totalCalculatedDiscount
      ) {
        const difference = roundedTotalDiscount - totalCalculatedDiscount;
        const lastItem = this.sold_list[this.sold_list.length - 1];
        lastItem.si_discount =
          Math.round((lastItem.si_discount + difference) * 100000) / 100000;
      }

      this.sold_list = [...this.sold_list];
    },
    recalculateDiscounts() {
      // Recalculate discounts when cart changes
      if (typeof this.total_discount === "number" && this.total_discount > 0) {
        this.applyTotalDiscount();
      } else if (this.percentage_discount > 0) {
        this.applyPercentageDiscount();
      }
    },
    calculateInitialTotalDiscount() {
      // Do not overwrite while user is typing a decimal (e.g. "1.", "1.05")
      if (typeof this.total_discount === "string") {
        return;
      }
      if (this.sold_list.length > 0) {
        let totalDiscount = 0;
        for (const item of this.sold_list) {
          totalDiscount += item.si_discount || 0;
        }
        if (totalDiscount > 0 && this.percentage_discount === 0) {
          if (this.cur_id == 2) {
            this.total_discount = parseFloat(totalDiscount.toFixed(5));
          } else {
            this.total_discount = Math.round(totalDiscount);
          }
        }
      }
    },
    checkHowMuchItemInCard(i_id) {
      for (const item of this.sold_list) {
        if (item.i_id == i_id) {
          return item.si_quantity;
        }
      }
      return 0;
    },
    removeItem(i_id) {
      for (const item of this.sold_list) {
        if (item.i_id == i_id) {
          this.sold_list.splice(this.sold_list.indexOf(item), 1);
          break;
        }
      }

      // Recalculate discounts after removing item
      this.recalculateDiscounts();
    },
    focusBarcodeInput() {
      const barcodeInputRef = this.$refs.barcodeInput;
      if (!barcodeInputRef) return;

      const inputEl =
        (barcodeInputRef.$el && barcodeInputRef.$el.querySelector("input")) ||
        barcodeInputRef.querySelector?.("input") ||
        null;

      if (inputEl && inputEl.focus) {
        inputEl.focus();
        if (inputEl.select) inputEl.select();
      }
    },
    async enterBarcode(e) {
      e.preventDefault();
      if (this.barcode == "") return;
      var item = this.filterItemsPos.find(
        (item) => item.i_barcode == this.barcode
      );
      if (item) {
        await this.addItemToInvoice(item, {
          skipQuantityFocus: true,
          refocusBarcodeAfterMs: 0,
        });
        this.barcode = "";
        this.$nextTick(() => this.focusBarcodeInput());
      } else {
        this.$refs.ResultDialog.show({
          message: this.$t("item_not_found_for_this_barcode"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        this.$nextTick(() => this.focusBarcodeInput());
      }
    },

    invoiceCustomerAddressForSalePosA4(overrideRow = null) {
      const customers = this.$store.state.customer.customers || [];
      const row = overrideRow || (this.cu_id
        ? customers.find((r) => String(r.cu_id) === String(this.cu_id))
        : null);
      return getEscapedInvoiceCustomerCityAddressLine(
        null,
        row,
        this.$t("invoice_customer_address_placeholder")
      );
    },
    async modalPrintIDA4(created_at, printSnapshot = null) {
      const snap =
        printSnapshot && typeof printSnapshot === "object"
          ? printSnapshot
          : {
              cu_id: this.cu_id,
              customer_name: this.customer_name,
              customer_phone_number: this.customer_phone_number,
            };
      const previousLocale = this.getCurrentLocaleValue();
      const targetLocale = this.printInvoiceLanguage || previousLocale;
      if (targetLocale !== previousLocale) {
        this.setCurrentLocaleValue(targetLocale);
        await this.$nextTick();
      }
      var res = await this.$store.dispatch("customer/getCurrentDebt", {
        cu_id: snap.cu_id,
        //invoice date
        created_at: created_at,
      });

      var beforeDebtIQD = res.balance_before_date.IQD;
      var beforeDebtUSD = res.balance_before_date.USD;
      //balance_up_to_and_including_date
      var balanceUpToAndIncludingDateIQD =
        res.balance_up_to_and_including_date.IQD;
      var balanceUpToAndIncludingDateUSD =
        res.balance_up_to_and_including_date.USD;
      let customerPrintRow = null;
      if (snap.cu_id != null && snap.cu_id !== "") {
        customerPrintRow = await this.$store.dispatch(
          "customer/fetchCustomerRowForPrint",
          snap.cu_id
        );
      }

      // Create a hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";

      document.body.appendChild(iframe);

      const printTotals = this.getInvoicePrintTotals();

      const invoiceContent = `
    <html>
    <head>
      <title>${this.settings.app_name}</title>
      <style>
        /* Define the Kurdish font and apply it to the body */
        @font-face {
          font-family: 'kurd';
          src: url('https://storage.wisesale.app/Rabar_022-Busi21Jx.ttf') format('truetype');
        }

        body {
          font-family: 'kurd', sans-serif;
          direction: rtl; /* Set text direction to right-to-left */
          text-align: right;

          -webkit-print-color-adjust: exact; /* Force printing of background colors */
          print-color-adjust: exact;
          background: white;
          margin: 0;
        }

        /* Set A4 dimensions and margins for print */
        @page {
          size: A4;

        }

        .my_font { font-family: 'kurd' !important; }

        /* General styles for the invoice layout */
        .invoice-container {
          max-width: 210mm;
          margin: 0 auto;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          padding: 10mm;
        }

        .headerBorder{
          border-bottom: none;
          padding-bottom: 10px;
          margin-bottom: 8px;
        }
        .invoice-header-sheet .top {
          display: grid;
          grid-template-columns: 360px 1fr;
          align-items: center;
          gap: 14px;
          direction: ltr;
        }
        .invoice-header-sheet .left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0;
          justify-self: start;
          padding-bottom: 2px;
        }
        .invoice-header-sheet .left-main {
          display: flex;
          align-items: flex-end;
          gap: 8px;
        }
        .invoice-header-sheet .logo {
          min-height: 108px;
          width: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          background: #fff;
          border-radius: 14px 4px 4px 14px;
        }
        .invoice-header-sheet .logo img {
          width: 100%;
          max-height: 102px;
          object-fit: contain;
          object-position: left center;
        }
        .invoice-header-sheet .accent {
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 12px;
          direction: ltr;
        }
        .invoice-header-sheet .accent .line {
          position: relative;
          height: 3px;
          display: block;
        }
        .invoice-header-sheet .accent .line::after {
          content: "";
          position: absolute;
          right: -8px;
          top: -1px;
          width: 10px;
          height: 5px;
          transform: skewX(-35deg);
          background: inherit;
        }
        .invoice-header-sheet .accent .line.dark {
          background: #2f4f6f;
          width: calc(50% - 6px);
        }
        .invoice-header-sheet .accent .line.red {
          background: #c00000;
          width: calc(50% - 6px);
        }
        .invoice-header-sheet .titles {
          text-align: right;
          direction: rtl;
          padding-right: 16px;
        }
        .invoice-header-sheet .titles h1 {
          margin: 0;
          font-size: 39px;
          color: #2f4f6f;
          line-height: 1;
          font-weight: 800;
          white-space: nowrap;
        }
        .invoice-header-sheet .titles h1 .inline-sub {
          font-size: 20px;
          color: #333;
          font-weight: 700;
          margin-right: 10px;
          white-space: nowrap;
        }
        .invoice-header-sheet .titles p {
          margin: 6px 0 0 0;
          font-size: 20px;
          color: #222;
          line-height: 1.2;
          font-weight: 700;
          text-align: center;
        }
        .invoice-header-sheet .titles .en {
          font-size: 16px;
          font-weight: 600;
          direction: ltr;
          text-align: center;
        }
        .invoice-header-sheet .no {
          font-size: 15px;
          color: #c00000;
          font-weight: 800;
          white-space: nowrap;
          text-align: left;
          margin-top: 0;
          margin-left: -4px;
        }
        .invoice-header-sheet .contact {
          margin-top: 8px;
          border-bottom: 1px solid #cfcfcf;
          padding: 8px 0;
          text-align: center;
          font-size: 15px;
          font-weight: 700;
          color: #333;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 18px;
          flex-wrap: wrap;
        }
        .invoice-header-sheet .contact .item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .invoice-header-sheet .contact .fb-badge {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #1877f2;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
        }
        .invoice-header-sheet .meta {
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          gap: 12px;
          font-size: 18px;
          font-weight: 700;
          color: #222;
          border-bottom: none;
          padding-bottom: 6px;
        }
        .invoice-header-sheet .meta.second {
          border-bottom: none;
          padding-bottom: 0;
          margin-top: 4px;
          justify-content: flex-start;
        }
        .summary-wrapper {
          width: 100%;
          display: block;
        }
        .customer-row {
          display: block;
          padding-bottom: 6px;
        }

        /* Details section styles */
        .details {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          font-size: 13px;
        }
        .details h3 {
          margin: 0 0 5px 0;
          font-size: 15px;
          color: #7165E3;
        }

        /* Table styles */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          font-size: 13px;
        }
        th, td {
          border: 1px solid #eee;
          padding: 7px;
          text-align: center;
        }
        th {
          background-color: #7165E3;
          color: white;
          white-space: pre-line;
          line-height: 1.15;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        /* Summary styles */
        .summary-box {
          margin-top: 20px;
          background: #f2f2f2;
          padding: 10px;
          border-radius: 5px;
        }
        .summary-box h3 {
          margin: 5px 0;
          color: #7165E3;
        }

        /* Footer styles */
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 14px;
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <!-- HEADER -->
        <div class="invoice-header-sheet headerBorder">
          <div class="top">
            <div class="left">
              <div class="left-main">
                <div class="logo">
                  <img src="${this.file_url + this.settings.set_logo}" alt="Company Logo" />
                </div>
                <div class="no">NO:${this.in_id || "-"}</div>
              </div>
            </div>
            <div class="titles">
              <h1>کۆگای بەهێز <span class="inline-sub">بۆ بازرگانی گشتی ئەلەمینیۆم</span></h1>
              <p class="en">Bahez store for the general aluminum trade</p>
              <p>مخزن بەهێز للتجارة العامة الألمنيوم</p>
            </div>
          </div>
          <div class="accent">
            <span class="line red"></span>
            <span class="line dark"></span>
          </div>
          <div class="contact">
            <span class="item"><span class="fb-badge"><i class="fab fa-facebook-f"></i></span> Bahez store</span>
            <span class="item"><i class="far fa-envelope"></i> Bahez.bakr80@gmail.com</span>
            <span class="item"><i class="fas fa-phone-alt"></i> ${this.settings.set_phone_number || ""}${this.settings.set_phone_number2 ? " - " + this.settings.set_phone_number2 : ""}</span>
          </div>
          <div class="meta">
            <div><b>السيد/ بەڕێز:</b> ${
        this.getCustomerDisplayLabel(
          snap.cu_id,
          snap.customer_name,
          snap.customer_phone_number
        )
      }</div>
            <div><b>تاریخ/ بەروار:</b> ${formatDate(created_at)}</div>
          </div>
          <div class="meta second">
            <div><b>العنوان/ ناونیشان:</b> ${this.invoiceCustomerAddressForSalePosA4(customerPrintRow)}</div>
          </div>
        </div>
        <!-- LINE ITEMS TABLE -->
        <table>
          <thead>
            <tr>
              <th>${this.$t("#")}</th>
              <th>${this.$t("item_name")}</th>
              <th>${this.$t("invoice_box_qty_print")}</th>
              <th>${this.$t("invoice_pcs_per_box_print")}</th>
              <th>${this.$t("invoice_box_price")}</th>
              <th>${this.$t("invoice_pieces_only")}</th>
              <th>${this.$t("invoice_piece_price")}</th>
              ${this.totalBonusItems > 0 ? `<th>${this.$t("bonus")}</th>` : ``}
              ${
                this.showCartDiscountColumn && this.sumDiscount > 0
                  ? `<th>${this.$t("discount")}</th>`
                  : ``
              }
              <th>${this.$t("total_price")}</th>
            </tr>
          </thead>
          <tbody>
            ${this.getSoldListByCurrentCurrency
              .map(
                (item) => `
              <tr>

                <td>${this.getSoldListByCurrentCurrency.indexOf(item) + 1}</td>
                <td>${this.getInvoiceItemName(item)}</td>
                <td>${this.getInvoiceBoxCountDisplay(item)}</td>
                <td>${this.getInvoicePiecesPerBoxDisplay(item)}</td>
                <td>${this.getInvoiceBoxPriceDisplay(item)}</td>
                <td>${this.formatNumber(this.getInvoicePiecesCount(item))}</td>
                <td>${this.formatPriceByCurrency(this.getInvoicePiecePriceFromBox(item))}</td>
                ${
                  this.totalBonusItems > 0
                    ? `<td>${item.si_bonus_quantity > 0 ? this.formatNumber(item.si_bonus_quantity) : '-'}</td>`
                    : ``
                }
                ${
                  this.showCartDiscountColumn && this.sumDiscount > 0
                    ? `<td>${this.formatNumber(item.si_discount)}</td>`
                    : ``
                }
                <td>${this.formatNumber(
                  item.si_quantity * this.getPriceByUnit(item) -
                    item.si_discount
                )}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
  ${
    this.in_note
      ? `
    <div class="summary-box" style="margin: 10px 0 !important; padding: 8px !important;">
      <h3 style="margin: 0 0 5px 0 !important;">${this.$t("note")}</h3>
      <p style="margin: 0 !important;">${this.in_note}</p>
    </div>
    `
      : ``
  }
         <div class='summary-wrapper'>
        <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%; direction: ltr; margin-top: 20px; gap: 16px;">
          <div style="background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box; direction: rtl; text-align: right;">
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 17px; font-weight: normal; color: #555;">${this.$t(
                "items_price"
              )}:</span>
              <span style="font-size: 17px; font-weight: normal; color: #333;">  ${this.formatPriceByCurrency(
                printTotals.itemsPrice
              )} ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}

              </span>
            </div>
            ${
              printTotals.discountTotal > 0
                ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 17px; font-weight: bold; color: #dc2626;">${this.$t(
                "discount"
              )}:</span>
              <span style="font-size: 17px; font-weight: bold; color: #dc2626;">${this.formatPriceByCurrency(
                printTotals.discountTotal
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            `
                : ``
            }
            <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 18px; color: #333;">
              <span style=" color: #555;">${this.$t("final_price")}:</span>
              <span style=" font-weight: bold;">${this.formatPriceByCurrency(
                printTotals.finalPrice
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            ${
              this.getInDue > 0
                ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 18px; font-weight: normal; color: #555;">${this.$t(
                "paid_money"
              )}:</span>
              <span style="font-size: 18px; font-weight: bold; color: #008000;">${this.formatPriceByCurrency(
                this.in_paid
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0">
              <span style="font-size: 18px; ; color: #555;">${this.$t(
                "debt"
              )}:</span>
              <span style="font-size: 18px; font-weight: bold;; color: red;">${this.formatPriceByCurrency(
                this.getInDue
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            `
                : ``
            }
          </div>
          ${
            beforeDebtIQD > 0 ||
            beforeDebtIQD < 0 ||
            beforeDebtUSD > 0 ||
            beforeDebtUSD < 0 ||
            balanceUpToAndIncludingDateIQD > 0 ||
            balanceUpToAndIncludingDateIQD < 0 ||
            balanceUpToAndIncludingDateUSD > 0 ||
            balanceUpToAndIncludingDateUSD < 0
              ? `
          <div style="background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box; min-width: 200px; direction: rtl; text-align: right;">
            <div style="padding: 4px 0;">
              <div style="font-size: 14px;">
                ${this.$t("balance_before")}:
              </div>
              <div style="font-size: 14px; font-weight: bold; color: red;">
                ${formatNumber(beforeDebtIQD)} ${this.$t("dinar")}
              </div>
              <div style="font-size: 14px; font-weight: bold; color: red;">
                ${formatNumber(beforeDebtUSD)} ${this.$t("dollar")}
              </div>
            </div>
            <div style="padding: 4px 0; margin-top: 10px;">
              <div style="font-size: 14px;">
                ${this.$t("balance_after")}:
              </div>
              <div style="font-size: 14px; font-weight: bold; color: red;">
                ${formatNumber(balanceUpToAndIncludingDateIQD)} ${this.$t("dinar")}
              </div>
              <div style="font-size: 14px; font-weight: bold; color: red;">
                ${formatNumber(balanceUpToAndIncludingDateUSD)} ${this.$t("dollar")}
              </div>
            </div>
          </div>
          `
              : `<div></div>`
          }
        </div>
        </div>

      </div>
    </body>
    </html>
  `;

      // Write the generated HTML to the iframe
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(invoiceContent);
      doc.close();

      // Wait a moment before printing
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.onafterprint = () => {
            if (targetLocale !== previousLocale) {
              this.setCurrentLocaleValue(previousLocale);
            }
          };
          iframe.contentWindow.print();
          // Cleanup
          if (targetLocale !== previousLocale) {
            this.setCurrentLocaleValue(previousLocale);
          }
        }, 50); // slight delay ensures content is loaded
      };
      this.getPosData();
      this.clearInvoice(false);
      this.$store.state.sale_pos.isBtnLoading = false;
    },
  },
};
</script>
<style scoped>
.pos-green-qty-input {
  -moz-appearance: textfield;
  appearance: textfield;
}

.pos-green-qty-input::-webkit-outer-spin-button,
.pos-green-qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
